import { useCallback, useEffect, useState, useRef, useMemo, type InputHTMLAttributes } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, ApiError, ReceiveCheckpointError, type ImportOrderReceiveResult, ImportOrder, ImportOrderItem, ImportOrderConflict, ImportOrderAnalytics, VatBackfillResult, Supplier, Category } from "../api";
import { formatVND, formatDate } from "../utils";
import { currentMonthRange, previousMonthRange, groupTrendByMonth, bucketTrendByDay, type TrendBucket } from "../utils/purchaseAnalytics";
import { classifyReceiveOutcome, receiveWarningForResult } from "../utils/receiveOutcome";
import { GlassPanel, KpiCard, Badge, Modal, toast, confirm, SkeletonTable, SkeletonKpi, ErrorState, EmptyState, FilePicker, Select, DataTable, type Column } from "../components";
import ImportAttachments from "../components/ImportAttachments";
import ImportOrderAiChat, { type ImportOrderAiChatHandle } from "../components/ImportOrderAiChat";
import { normalizeSearchText, searchItems } from "../search";
import { searchCatalog } from "../lib/productCatalog";

/** Rolling 7 ngày VN: age (calendar days) > 7 → đơn CŨ (hỏi bookOnly). Age 0..7 → đơn MỚI. */
const OLD_IMPORT_DAYS = 7;

/** YYYY-MM-DD theo lịch Asia/Ho_Chi_Minh (UTC+7, không DST). */
function vnTodayStr(now = new Date()): string {
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
  const vn = new Date(utcMs + 7 * 3_600_000);
  const y = vn.getFullYear();
  const m = String(vn.getMonth() + 1).padStart(2, "0");
  const d = String(vn.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function ymdToUtcDays(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
}

/** Đơn cũ nếu order_date cách hôm nay VN hơn 7 ngày lịch. */
function isOldImportOrder(orderDate: string | null | undefined, now = new Date()): boolean {
  const d = (orderDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  return ymdToUtcDays(vnTodayStr(now)) - ymdToUtcDays(d) > OLD_IMPORT_DAYS;
}

// Self-check: Jul 13 2026 VN → Jul 6 new (age 7), Jul 5 old (age 8)
{
  const t = new Date("2026-07-13T12:00:00+07:00");
  console.assert(isOldImportOrder("2026-07-06", t) === false, "age7 should be new");
  console.assert(isOldImportOrder("2026-07-05", t) === true, "age8 should be old");
  console.assert(isOldImportOrder("2026-07-13", t) === false, "today should be new");
}

type ActiveAi = { provider: string; model: string; source: "supplier" | "global" } | null;

type OrderForm = {
  order_date: string;
  supplier_name: string;
  supplier_phone: string;
  supplier_address: string;
  supplier_tax_id: string;
  supplier_bank_account: string;
  currency: string;
  exchange_rate: number;
  total_amount: number;
  paid_amount: number;
  due_date: string;
  note: string;
  goods_type: "vn" | "vat" | "bangke" | "tieungach";
  vat_rate: number;
  invoice_no: string;
  extra_fee: number;
  items: OrderFormItem[];
};

type OrderFormItem = {
  /** import_order_items.id — giữ khi edit để backend detect unmap/remap sau rename */
  line_id?: number | null;
  /** Mã/Code cột SKU trên hóa đơn NCC — KHÔNG được bỏ: 2 mã khác nhau cùng tên SP phải là 2 dòng. */
  sku?: string | null;
  item_name: string;
  quantity: number;
  unit_cost: number;
  is_vat: boolean;
  is_bangke: boolean;
  mapped_item_id?: string | null;
  mapped_item_name?: string | null;
  sell_price?: number | null;
  is_new_product?: boolean;
  category_id?: string | null;
  pack_count?: number | null;
  retail_split?: number | null;
  /** UI only: mỗi lốc mấy cái (vd 10) → tự tính total_qty */
  per_pack?: number | null;
  /** UI only: tổng số cái thực nhập (mặc định = quantity × per_pack, nhưng user có thể sửa xuống nếu xài 1) */
  total_qty?: number | null;
  /** NL (nguyên liệu) — D1-only, KHÔNG đẩy Loyverse POS */
  is_material?: boolean;
  /** Đơn vị tính NL: kg/g/ml/l/pcs */
  unit?: string | null;
  /** SP gốc khi dòng thuộc nhóm gộp. Chỉ đọc — không gửi lại. */
  merge_target?: { item_id: string; item_name: string; sku: string | null } | null;
};

const emptyItem = (): OrderFormItem => ({
  line_id: null,
  sku: null,
  item_name: "",
  quantity: 1,
  unit_cost: 0,
  is_vat: false,
  is_bangke: false,
  mapped_item_id: null,
  mapped_item_name: null,
  is_material: false,
  unit: null,
  category_id: null,
});

/** Retail piece count for cost/% lời. Prefer explicit total_qty when >0. */
function retailPieceQty(i: OrderFormItem): number {
  const t = Number(i.total_qty);
  if (t > 0) return t;
  const pack = Number(i.per_pack) || 0;
  const qty = Number(i.quantity) || 0;
  if (pack > 0) return qty * pack;
  return qty;
}
function lineTotalCost(i: OrderFormItem): number {
  return (Number(i.quantity) || 0) * (Number(i.unit_cost) || 0);
}
function costPerRetailPiece(i: OrderFormItem, feePerUnit = 0): number {
  const n = retailPieceQty(i);
  if (n <= 0) return 0;
  return lineTotalCost(i) / n + feePerUnit;
}

// total_qty=12, qty=2, unit=88000 → cost/piece ~14666.67; sell 30000 → margin ~+104%/+105%
{
  const s: OrderFormItem = { item_name: "x", quantity: 2, unit_cost: 88000, is_vat: false, is_bangke: false, total_qty: 12 };
  const c = costPerRetailPiece(s);
  console.assert(Math.abs(c - 176000 / 12) < 0.01, "cost/piece prefers total_qty");
  const m = Math.round((30000 / c - 1) * 100);
  console.assert(m === 104 || m === 105, "margin ~+104/+105");
  console.assert(retailPieceQty({ ...s, total_qty: null, per_pack: null }) === 2, "fallback qty");
}

const MERGE_LOCK_REASON: Record<string, string> = {
  QTY_SOLD_LOT: "Lô đã bán, không sửa số lượng được",
  QTY_BOM_CONSUMED: "Lô đã dùng cho nguyên liệu",
  QTY_AMBIGUOUS_LOT: "Không xác định được lô của dòng này",
  COST_AMBIGUOUS_LINES: "Các dòng cùng sản phẩm đang lệch giá vốn",
  MATERIAL_LINE_LOCKED: "Dòng nguyên liệu chưa hỗ trợ sửa",
  REMAP_LOCKED: "Không đổi được sản phẩm map",
  ADD_REMOVE_LINE_LOCKED: "Không thêm/xoá dòng trên đơn đã gộp",
  NEW_PRODUCT_LOCKED: "Không tạo sản phẩm mới từ đơn đã gộp",
  GOODS_TYPE_LOCKED: "Không đổi loại đơn",
  ORDER_DATE_LOCKED: "Không đổi ngày nhập",
  VAT_FLAG_LOCKED: "Cờ thuế chốt theo lô, không sửa ở đơn đã gộp",
  BANGKE_FLAG_LOCKED: "Cờ thuế chốt theo lô, không sửa ở đơn đã gộp",
};

function mergeTargetLabel(t: { item_name: string; sku: string | null }): string {
  return `Đã gộp → ${t.item_name}${t.sku ? ` (${t.sku})` : ""}`;
}

/** One id per user save. Reuse on network/5xx retry; new id after success, 4xx, or sheet reset. */
function newSaveRequestId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `save-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 409 PATCH đơn gộp: giữ message server, gắn số dòng khi có line_id. */
function formatImportConflict(payload: ImportOrderConflict, items: Array<{ line_id?: number | null }>): string[] {
  const head = (payload.error || "Không lưu được đơn đã gộp").trim();
  const lines = (payload.reasons || []).map((r) => {
    const idx = r.line_id != null ? items.findIndex((it) => String(it.line_id ?? "") === String(r.line_id)) : -1;
    const prefix = idx >= 0 ? `Dòng ${idx + 1}: ` : r.line_id ? `Dòng #${r.line_id}: ` : "";
    const text = (r.message || "").trim() || MERGE_LOCK_REASON[r.code] || r.code;
    return prefix + text;
  });
  return [head, ...lines].filter(Boolean);
}
{
  const rows = formatImportConflict(
    { error: "Không sửa được", reasons: [{ line_id: "7", code: "QTY_SOLD_LOT", message: "" }, { line_id: null, code: "X", message: "Lý do lạ" }] },
    [{ line_id: 7 }],
  );
  console.assert(rows[0] === "Không sửa được" && rows[1].startsWith("Dòng 1:") && rows[1].includes("Lô đã bán") && rows[2] === "Lý do lạ", "conflict lines");
  console.assert(mergeTargetLabel({ item_name: "A", sku: null }) === "Đã gộp → A", "merge label no sku");
  const locked = formatImportConflict(
    { error: "Không sửa được", reasons: [{ line_id: "7", code: "VAT_FLAG_LOCKED", message: "" }, { line_id: null, code: "BANGKE_FLAG_LOCKED", message: "" }] },
    [{ line_id: 7 }],
  );
  console.assert(locked[1].includes("Cờ thuế chốt theo lô") && locked[2].includes("Cờ thuế chốt theo lô"), "flag lock text");
}

/** Lọc ký tự nhập số: chỉ số, 1 dấu `-` đầu, 1 dấu `.` */
function sanitizeNumInput(s: string): string {
  return s
    .replace(/[^\d.-]/g, "")
    .replace(/(?!^)-/g, "")
    .replace(/(\..*)\./g, "$1");
}

// Self-check: gõ Telex/ký tự lạ bị lọc, xóa trắng vẫn ra "" (không snap về số)
{
  console.assert(sanitizeNumInput("12aâ") === "12", "lọc chữ");
  console.assert(sanitizeNumInput("") === "", "cho phép rỗng");
  console.assert(sanitizeNumInput("1.2.3") === "1.23", "1 dấu chấm");
  console.assert(sanitizeNumInput("-1-2") === "-12", "1 dấu trừ đầu");
}

/**
 * Ô số cho dòng SP: type=text + inputMode để Telex IME (Win11) không nhảy số,
 * và giữ draft string nên xóa trắng được (không bị giá trị suy ra snap lại ngay).
 * Chốt số ngay khi gõ; rỗng/không hợp lệ thì chờ blur mới chốt `emptyValue`.
 */
function NumField({
  value,
  onCommit,
  emptyValue,
  ...rest
}: {
  value: number | null | undefined;
  onCommit: (v: number | null) => void;
  /** Chốt khi blur mà ô đang rỗng: 1 cho SL, 0 cho tiền, null nếu optional */
  emptyValue: number | null;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "onBlur" | "type">) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={draft ?? (value == null ? "" : String(value))}
      onChange={(e) => {
        const raw = sanitizeNumInput(e.target.value);
        setDraft(raw);
        const n = Number(raw);
        if (raw !== "" && Number.isFinite(n)) onCommit(n);
      }}
      onBlur={() => {
        const raw = draft;
        setDraft(null);
        if (raw !== null && (raw === "" || !Number.isFinite(Number(raw)))) onCommit(emptyValue);
      }}
      {...rest}
    />
  );
}

type GoodsType = OrderForm["goods_type"];

function itemFlagsForGoodsType(gt: GoodsType): Pick<OrderFormItem, "is_vat" | "is_bangke"> {
  return {
    is_vat: gt === "vat",
    is_bangke: gt === "bangke" || gt === "vn",
  };
}

const GOODS_TYPES: GoodsType[] = ["vn", "vat", "bangke", "tieungach"];

const GOODS_TYPE_LABEL: Record<string, string> = {
  vn: "Hàng VN",
  vat: "Hóa đơn VAT",
  bangke: "Bảng kê 01/TNDN",
  tieungach: "Tiểu ngạch (TQ)",
};

/** Merge AI form edit response onto current form — keep UI-only + mapping when AI omits. */
function normalizeAiForm(aiForm: any, prev: OrderForm): OrderForm {
  const gtRaw = aiForm?.goods_type;
  const goods_type: GoodsType =
    typeof gtRaw === "string" && (GOODS_TYPES as string[]).includes(gtRaw)
      ? (gtRaw as GoodsType)
      : prev.goods_type;
  const prevItems = prev.items || [];
  const aiItems: any[] = Array.isArray(aiForm?.items) ? aiForm.items : prevItems;

  const items: OrderFormItem[] = aiItems.map((raw, idx) => {
    const p = prevItems[idx];
    const name = String(raw?.item_name ?? p?.item_name ?? "").trim();
    // re-derive is_vat/is_bangke from goods_type (form-level; item goods_type if AI sent)
    const itemGt =
      typeof raw?.goods_type === "string" && (GOODS_TYPES as string[]).includes(raw.goods_type)
        ? (raw.goods_type as GoodsType)
        : goods_type;
    const itemFlags = itemFlagsForGoodsType(itemGt);
    // Keep mapped_item_id if AI omitted/null wrongly but same item_name at index
    let mapped_item_id: string | null =
      raw?.mapped_item_id !== undefined && raw?.mapped_item_id !== null
        ? String(raw.mapped_item_id)
        : null;
    if (!mapped_item_id && p?.mapped_item_id && name && p.item_name?.trim() === name) {
      mapped_item_id = p.mapped_item_id;
    }
    return {
      // AI không biết line_id — giữ prev theo index; dòng mới → null
      line_id: p?.line_id ?? null,
      // Mã NCC: AI trả về → ưu tiên; omit → giữ của dòng cũ cùng index.
      sku: raw?.sku !== undefined ? (raw.sku === null || raw.sku === "" ? null : String(raw.sku)) : p?.sku ?? null,
      item_name: name,
      quantity: Number(raw?.quantity ?? p?.quantity ?? 1) || 1,
      unit_cost: Number(raw?.unit_cost ?? p?.unit_cost ?? 0) || 0,
      is_vat: itemFlags.is_vat,
      is_bangke: itemFlags.is_bangke,
      mapped_item_id,
      mapped_item_name:
        raw?.mapped_item_name !== undefined && raw?.mapped_item_name !== null
          ? raw.mapped_item_name
          : mapped_item_id === p?.mapped_item_id
            ? p?.mapped_item_name ?? null
            : raw?.mapped_item_name ?? null,
      sell_price: raw?.sell_price !== undefined ? raw.sell_price : p?.sell_price ?? null,
      is_new_product: raw?.is_new_product !== undefined ? !!raw.is_new_product : p?.is_new_product,
      category_id: raw?.category_id !== undefined ? raw.category_id : p?.category_id ?? null,
      pack_count: raw?.pack_count !== undefined ? raw.pack_count : p?.pack_count ?? null,
      retail_split: raw?.retail_split !== undefined ? raw.retail_split : p?.retail_split ?? null,
      // AI gửi null/"" rõ ràng = user yêu cầu xóa Mỗi/Tổng → clear; omit → giữ prev
      per_pack: raw?.per_pack !== undefined ? (raw?.per_pack === null || raw?.per_pack === "" ? null : Number(raw.per_pack) || null) : p?.per_pack ?? null,
      total_qty: raw?.total_qty !== undefined ? (raw?.total_qty === null || raw?.total_qty === "" ? null : Number(raw.total_qty) || null) : p?.total_qty ?? null,
      is_material: raw?.is_material !== undefined ? !!raw.is_material : p?.is_material ?? false,
      unit: raw?.unit !== undefined ? raw.unit : p?.unit ?? null,
    };
  });

  const num = (v: unknown, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const str = (v: unknown, fallback: string) =>
    v === undefined || v === null ? fallback : String(v);

  return {
    order_date: str(aiForm?.order_date, prev.order_date) || prev.order_date,
    supplier_name: str(aiForm?.supplier_name, prev.supplier_name),
    supplier_phone: str(aiForm?.supplier_phone, prev.supplier_phone),
    supplier_address: str(aiForm?.supplier_address, prev.supplier_address),
    supplier_tax_id: str(aiForm?.supplier_tax_id, prev.supplier_tax_id),
    supplier_bank_account: str(aiForm?.supplier_bank_account, prev.supplier_bank_account),
    currency: str(aiForm?.currency, prev.currency) || prev.currency,
    exchange_rate: num(aiForm?.exchange_rate, prev.exchange_rate),
    total_amount: num(aiForm?.total_amount, prev.total_amount),
    paid_amount: num(aiForm?.paid_amount, prev.paid_amount),
    due_date: str(aiForm?.due_date, prev.due_date),
    note: str(aiForm?.note, prev.note),
    goods_type,
    vat_rate: num(aiForm?.vat_rate, prev.vat_rate),
    invoice_no: str(aiForm?.invoice_no, prev.invoice_no),
    extra_fee: num(aiForm?.extra_fee, prev.extra_fee),
    items: items.length ? items : prev.items,
  };
}

type DetailEdit = {
  supplier_name: string;
  supplier_phone: string;
  supplier_address: string;
  supplier_tax_id: string;
  supplier_bank_account: string;
  order_date: string;
  total_amount: number;
  paid_amount: number;
  note: string;
  invoice_no: string;
};

function seedDetailEdit(o: ImportOrder): DetailEdit {
  return {
    supplier_name: o.supplier_name || "",
    supplier_phone: o.supplier_phone || "",
    supplier_address: o.supplier_address || "",
    supplier_tax_id: o.supplier_tax_id || "",
    supplier_bank_account: o.supplier_bank_account || "",
    order_date: (o.order_date || "").slice(0, 10),
    total_amount: Number(o.total_amount) || 0,
    paid_amount: Number(o.paid_amount) || 0,
    note: o.note || "",
    invoice_no: o.invoice_no || "",
  };
}

const emptyForm = (goodsType: GoodsType = "tieungach"): OrderForm => ({
  // ponytail: arg chỉ dùng để default khi trang truyền defaultGoodsType (legacy/back-compat).
  // Khi tạo đơn mới từ ImportVnPage nay không lockGoodsType, user sẽ tự chọn trong form.
  order_date: new Date().toISOString().slice(0, 10),
  supplier_name: "",
  supplier_phone: "",
  supplier_address: "",
  supplier_tax_id: "",
  supplier_bank_account: "",
  currency: "VND",
  exchange_rate: 1,
  total_amount: 0,
  paid_amount: 0,
  due_date: "",
  note: "",
  goods_type: goodsType,
  vat_rate: goodsType === "vat" ? 8 : 0,
  invoice_no: "",
  extra_fee: 0,
  items: [emptyItem()],
});

export type ImportOrderPageProps = {
  /** Preset khi tạo đơn mới */
  defaultGoodsType?: GoodsType;
  /** Giới hạn lựa chọn loại hàng trong form */
  allowedGoodsTypes?: GoodsType[];
  /** Khóa loại hàng (legacy/custom path) */
  lockGoodsType?: boolean;
  /** Nhấn mạnh upload/xem HĐ */
  emphasizeInvoice?: boolean;
  /** Chỉ xem — ẩn nút Tạo đơn (dùng cho view đối chiếu thuế) */
  readOnly?: boolean;
  panelTitle?: string;
  panelDescription?: string;
};

const GOODS_TYPE_LABELS: Record<GoodsType, string> = {
  tieungach: "Tiểu ngạch (China) — không lên sổ thuế",
  vn: "Hàng VN (bảng kê)",
  vat: "Hóa đơn VAT — có hóa đơn đỏ",
  bangke: "Bảng kê 01/TNDN — hàng VN không hóa đơn",
};

// Badge gọn cho table (desktop) — full label vẫn xem được trong detail modal.
const GOODS_TYPE_SHORT: Record<GoodsType, string> = {
  tieungach: "TQ",
  vn: "VN",
  vat: "VAT",
  bangke: "BK",
};

/** Trang Đơn nhập hàng (P1-6) — FULL; có thể nhúng trong ImportVnPage qua props */
export default function ImportOrderPage({
  defaultGoodsType = "tieungach",
  allowedGoodsTypes,
  lockGoodsType = false,
  emphasizeInvoice = false,
  readOnly = false,
  panelTitle = "Đơn nhập hàng",
  panelDescription = "Tạo đơn → khi hàng về kho bấm \"Đã nhập kho\" để xác nhận.",
}: ImportOrderPageProps = {}) {
  const [orders, setOrders] = useState<ImportOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<OrderForm>(() => emptyForm(defaultGoodsType));
  const [submitting, setSubmitting] = useState(false);
  const [saveConflict, setSaveConflict] = useState<string[] | null>(null);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [supplierList, setSupplierList] = useState<Supplier[]>([]);
  const [supplierSuggestOpen, setSupplierSuggestOpen] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  // Detail modal
  const [detailOrder, setDetailOrder] = useState<ImportOrder | null>(null);
  // Header fields editable in detail modal (PATCH partial, no items).
  const [detailEdit, setDetailEdit] = useState<DetailEdit>({
    supplier_name: "", supplier_phone: "", supplier_address: "", supplier_tax_id: "",
    supplier_bank_account: "", order_date: "", total_amount: 0, paid_amount: 0, note: "", invoice_no: "",
  });
  const [detailSaving, setDetailSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  const markingRef = useRef(false);
  const [deleting, setDeleting] = useState(false);
  // Dashboard nhập hàng — API analytics toàn bộ dữ liệu (không phụ thuộc list 100 đơn).
  type AnalyticsMode = "month" | "prev" | "all" | "custom";
  const [analytics, setAnalytics] = useState<ImportOrderAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [analyticsRange, setAnalyticsRange] = useState<{ mode: AnalyticsMode; from: string; to: string }>(
    () => ({ mode: "month", ...currentMonthRange() }),
  );
  // Search products for autocomplete
  const [searchResults, setSearchResults] = useState<Array<{ id: string; item_name: string; sku?: string; cost?: number; default_price?: number }>>([]);
  const [activeItemIdx, setActiveItemIdx] = useState<number | null>(null);
  const [aiPricingIdx, setAiPricingIdx] = useState<number | null>(null);
  const [mapResults, setMapResults] = useState<Record<number, Array<{ id: string; item_name: string; sku?: string; default_price?: number }>>>({});
  // AI per-supplier
  const [activeAi, setActiveAi] = useState<ActiveAi>(null);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [aiChatKey, setAiChatKey] = useState(0);
  const [scanAttachOnce, setScanAttachOnce] = useState(false);
  const productsSectionRef = useRef<HTMLDivElement>(null);
  const aiChatRef = useRef<ImportOrderAiChatHandle>(null);
  const saveRequestIdRef = useRef<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // In tem cho các SP trong đơn (theo SL nhập) → sang trang in tem, chọn sẵn.
  // CHỈ truyền item_id + qty; trang in tem tự load SKU/barcode/giá THẬT từ POS.
  const handlePrintLabels = (order: ImportOrder) => {
    // 1 DÒNG ĐƠN = 1 ENTRY — KHÔNG gộp theo mapped_item_id: user dán tem theo đúng STT của đơn.
    // lineKey (= import_order_items.id) tách 2 dòng cùng 1 SP để Map của trang in không ghi đè nhau.
    const list = (order.items || [])
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => !!it.mapped_item_id)
      .map(({ it, idx }) => ({
        id: it.mapped_item_id as string,
        qty: Math.max(1, Math.floor(it.quantity || 1)),
        lineKey: it.id != null ? `l${it.id}` : `i${idx}`,
      }));
    if (list.length === 0) { toast.error("Đơn chưa có SP nào map/tạo trên POS để in tem"); return; }
    sessionStorage.setItem("barcode_preselect", JSON.stringify(list));
    sessionStorage.setItem("barcode_preselect_mode", "qty");
    navigate("/ops/barcode");
  };

  const resetScanUi = () => {
    setShowMoreDetails(false);
    setScanAttachOnce(false);
    setAiChatKey((k) => k + 1);
  };

  const clearSaveRequestId = () => {
    saveRequestIdRef.current = null;
  };

  // Đơn đang tạo (draft) = có dữ liệu đã nhập/chỉnh sửa chưa lưu. User bấm close/outside/Esc → confirm.
  // editingId (đơn đã lưu PATCH) không cần warn vì đã persist; chỉ warn khi tạo mới.
  const hasDraft = (): boolean => {
    if (editingId) return false;  // patch đơn đã có → an tất
    const hasItems = form.items.some((it) => it.item_name.trim() || it.mapped_item_id);
    const hasSupplier = form.supplier_name.trim().length > 0;
    const hasAmount = form.total_amount > 0;
    const hasScan = !!invoiceFile;
    return hasItems || hasSupplier || hasAmount || hasScan;
  };

  // Modal close có chặn: nếu đang draft → confirm trước khi close.
  const attemptClose = async () => {
    if (hasDraft()) {
      const ok = await confirm({
        title: "Hủy đơn đang tạo?",
        message: "Đơn chưa lưu sẽ mất. Tiếp tục đóng?",
        confirmLabel: "Hủy đơn",
        cancelLabel: "Tiếp tục nhập",
      });
      if (!ok) return;
    }
    clearBulkSelect();
    setShowModal(false);
    setEditingId(null);
    clearSaveRequestId();
    resetScanUi();
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFrom, dateTo, searchDebounced]);

  // Debounce search input → 250ms (giống ProductsPage/CommandPalette pattern)
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Desktop DataTable vs mobile card list (giống ProductsPage viewMode swap)
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // FabAction "Quét HĐ" → ?action=scan → mở modal + focus attach trong chat
  useEffect(() => {
    if (searchParams.get("action") === "scan") {
      setEditingId(null);
      setForm(emptyForm(defaultGoodsType));
      setInvoiceFile(null);
      clearSaveRequestId();
      resetScanUi();
      setScanAttachOnce(true);
      setShowModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, defaultGoodsType]);

  // Deeplink ?order=<id> (từ tab Hóa đơn VAT) → mở modal chi tiết đơn
  useEffect(() => {
    const orderId = searchParams.get("order");
    if (!orderId || orders.length === 0) return;
    const found = orders.find((o) => String(o.id) === orderId);
    if (found) setDetailOrder(found);
    setSearchParams({}, { replace: true });
  }, [searchParams, orders]);

  // Seed detailEdit khi mở detail modal — bind input thay cho detailOrder read-only.
  useEffect(() => {
    if (detailOrder) setDetailEdit(seedDetailEdit(detailOrder));
  }, [detailOrder]);

  // Load AI config when supplier changes in form
  const loadSupplierAi = async (supplierName: string) => {
    if (!supplierName.trim()) { setActiveAi(null); return; }
    try {
      // Find supplier by name from list
      const listRes = await api.suppliers.list();
      const supplier = (listRes.suppliers || []).find(
        (s) => normalizeSearchText(s.name) === normalizeSearchText(supplierName),
      );
      if (supplier) {
        const aiRes = await api.suppliers.getAi(supplier.id);
        if (aiRes.provider && aiRes.model) {
          setActiveAi({ provider: aiRes.provider, model: aiRes.model, source: "supplier" });
          return;
        }
      }
      // Fallback to global
      const globalRes = await api.ai.configMulti();
      if (globalRes.active && globalRes.model) {
        setActiveAi({ provider: globalRes.active, model: globalRes.model, source: "global" });
      } else {
        setActiveAi(null);
      }
    } catch {
      setActiveAi(null);
    }
  };

  // Load danh sách NCC 1 lần để gợi ý autocomplete
  useEffect(() => {
    api.suppliers.list()
      .then((r) => setSupplierList(r.suppliers || []))
      .catch(() => {});
  }, []);

  // Load danh sách danh mục (gán khi hàng mới)
  const [categories, setCategories] = useState<Category[]>([]);
  useEffect(() => {
    api.categories.list()
      .then((r) => setCategories(r.categories || []))
      .catch(() => {});
  }, []);

  // Bulk select hàng mới: chọn nhiều dòng + đặt tên/danh mục chung
  const [selectedIdxs, setSelectedIdxs] = useState<Set<number>>(new Set());
  const [bulkName, setBulkName] = useState("");
  const [bulkCatId, setBulkCatId] = useState("");
  const clearBulkSelect = () => {
    setSelectedIdxs(new Set());
    setBulkName("");
    setBulkCatId("");
  };

  // Đơn VAT / Bảng kê cần thông tin NCC đầy đủ → tự mở phần "Chi tiết thêm"
  useEffect(() => {
    if (form.goods_type === "vat" || form.goods_type === "bangke") {
      setShowMoreDetails(true);
    }
  }, [form.goods_type]);

  // Gợi ý NCC khớp tên đang gõ (tối đa 8)
  const supplierSuggestions = (() => {
    const q = form.supplier_name.trim();
    if (!q) return [] as Supplier[];
    return searchItems(supplierList, q, (s) => ({ name: s.name }))
      .slice(0, 8)
      .map(({ item }) => item);
  })();
  const supplierExactMatch = supplierList.some(
    (s) => normalizeSearchText(s.name) === normalizeSearchText(form.supplier_name),
  );

  // Chọn 1 NCC từ gợi ý → auto-fill các ô + lưu supplier_id
  const pickSupplier = (s: Supplier) => {
    setSelectedSupplierId(s.id);
    setForm((prev) => ({
      ...prev,
      supplier_name: s.name,
      supplier_phone: s.phone || prev.supplier_phone,
      supplier_address: s.address || prev.supplier_address,
      supplier_tax_id: s.tax_id || prev.supplier_tax_id,
      supplier_bank_account: s.bank_account || prev.supplier_bank_account,
    }));
    setSupplierSuggestOpen(false);
    loadSupplierAi(s.name);
  };

  // Seq guard: tránh stale response khi user đổi filter nhanh (date/status fire ngay, search debounce 250ms).
  const loadSeq = useRef(0);
  const loadOrders = async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const r = await api.importOrders.list({
        status: statusFilter || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        q: searchDebounced || undefined,
      });
      if (seq !== loadSeq.current) return;  // stale → drop
      setOrders(r.orders || []);
      return r.orders || [];
    } catch (e: unknown) {
      if (seq !== loadSeq.current) return;
      setError((e as Error).message);
      return undefined;
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  // Analytics = toàn bộ dữ liệu đã nhập (không phụ thuộc list 100 đơn, không phụ thuộc filter chữ/trạng thái).
  const analyticsAbort = useRef<AbortController | null>(null);
  const analyticsSeq = useRef(0);
  const loadAnalytics = useCallback(async () => {
    const seq = ++analyticsSeq.current;
    analyticsAbort.current?.abort();
    const ctrl = new AbortController();
    analyticsAbort.current = ctrl;
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const r = await api.importOrders.analytics({
        ...(analyticsRange.from ? { from: analyticsRange.from } : {}),
        ...(analyticsRange.to ? { to: analyticsRange.to } : {}),
        signal: ctrl.signal,
      });
      if (seq !== analyticsSeq.current || ctrl.signal.aborted) return;
      setAnalytics(r);
    } catch (e: unknown) {
      if (seq !== analyticsSeq.current || ctrl.signal.aborted) return;
      // Lỗi không được hiển thị như số 0 — giữ error riêng.
      setAnalytics(null);
      setAnalyticsError((e as Error)?.message || "Không tải được số liệu nhập hàng");
    } finally {
      if (seq === analyticsSeq.current) setAnalyticsLoading(false);
    }
  }, [analyticsRange]);

  useEffect(() => {
    void loadAnalytics();
    return () => analyticsAbort.current?.abort();
  }, [loadAnalytics]);

  // Đang ở chế độ "Tùy chọn" → bám theo ngày của bộ lọc list.
  useEffect(() => {
    setAnalyticsRange((prev) =>
      prev.mode === "custom" ? { mode: "custom", from: dateFrom, to: dateTo } : prev,
    );
  }, [dateFrom, dateTo]);

  /** Mutation xong: refresh list + dashboard (analytics không phụ thuộc filter list). */
  const reloadAfterMutation = async () => {
    await loadOrders();
    void loadAnalytics();
  };

  const scopedOrders = orders;

  // Status/from/to/q đã lọc server-side; ImportVnPage nay không còn tab scoping.
  const filtered = scopedOrders;

  // Default: newest created_at first (desktop DataTable + mobile cards share base order).
  const sortedForDisplay = useMemo(
    () =>
      [...filtered].sort(
        (a, b) =>
          String(b.created_at || "").localeCompare(String(a.created_at || "")) || b.id - a.id,
      ),
    [filtered],
  );

  const goodsTypeOptions: GoodsType[] = allowedGoodsTypes?.length
    ? allowedGoodsTypes
    : (["tieungach", "vat", "bangke"] as GoodsType[]);

  // Một loại hàng duy nhất → giữ form.goods_type khớp (không cần dropdown)
  useEffect(() => {
    if (!showModal || lockGoodsType || goodsTypeOptions.length !== 1) return;
    const only = goodsTypeOptions[0];
    setForm((prev) => (prev.goods_type === only ? prev : { ...prev, goods_type: only }));
  }, [showModal, lockGoodsType, goodsTypeOptions]);

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyForm(defaultGoodsType));
    setInvoiceFile(null);
    clearSaveRequestId();
    resetScanUi();
    clearBulkSelect();
    setShowModal(true);
  };

  // Mở đơn ĐÃ LƯU bằng form đầy đủ (sửa được: ship, lẻ ra, giá bán, map/hàng mới).
  const openEditOrder = (order: ImportOrder) => {
    clearBulkSelect();
    clearSaveRequestId();
    setEditingId(order.id);
    setForm({
      order_date: (order.order_date || "").slice(0, 10),
      supplier_name: order.supplier_name || "",
      supplier_phone: order.supplier_phone || "",
      supplier_address: order.supplier_address || "",
      supplier_tax_id: order.supplier_tax_id || "",
      supplier_bank_account: order.supplier_bank_account || "",
      currency: "VND",
      exchange_rate: 1,
      total_amount: order.total_amount || 0,
      paid_amount: order.paid_amount || 0,
      due_date: (order.due_date || "").slice(0, 10),
      note: order.note || "",
      goods_type: (order.goods_type as GoodsType) || defaultGoodsType,
      vat_rate: order.vat_rate || 0,
      invoice_no: order.invoice_no || "",
      extra_fee: 0,
      items: (order.items || []).map((it) => ({
        line_id: it.id ?? null,
        sku: it.sku ?? null,
        item_name: it.item_name,
        quantity: it.quantity,
        unit_cost: it.unit_cost,
        is_vat: !!it.is_vat,
        is_bangke: !!it.is_bangke,
        mapped_item_id: it.mapped_item_id || null,
        mapped_item_name: it.mapped_item_id ? (it.mapped_item_name || it.item_name) : null,
        sell_price: it.sell_price ?? null,
        // Dòng ĐÃ map = SP cũ, KHÔNG phải hàng mới (dù cờ is_new sót lại từ receive cũ).
        // Để is_new=1 ở dòng đã map sẽ ẩn ô map → bấm nhập kho lại đẻ SP mới + xóa map cũ.
        is_new_product: it.mapped_item_id ? false : !!it.is_new_product,
        category_id: ((it as ImportOrderItem & { category_id?: string | null }).category_id) ?? null,
        retail_split: null,
        is_material: !!it.is_material,
        unit: it.unit || null,
        merge_target: it.merge_target ?? null,
      })),
    });
    setInvoiceFile(null);
    resetScanUi();
    setDetailOrder(null);
    setShowModal(true);
  };

  const viewInvoice = async (order: ImportOrder) => {
    if (!order.invoice_file_id) return;
    try {
      const res = await fetch(api.importOrders.invoiceUrl(order.id), { headers: api.authHeaders() });
      if (!res.ok) throw new Error("Không tải được hóa đơn");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Không mở được hóa đơn");
    }
  };

  const statusVariant = (s: string): "ok" | "warn" | "bad" | "info" | "" => {
    const map: Record<string, "ok" | "warn" | "bad" | "info" | ""> = {
      pending: "warn",
      imported: "ok",
      cancelled: "bad",
      draft: "info",
    };
    return map[s] || "";
  };

  const statusText = (s: string) => {
    const map: Record<string, string> = {
      pending: "Chờ nhập",
      imported: "Đã nhập",
      cancelled: "Đã hủy",
      draft: "Draft",
    };
    return map[s] || s;
  };

  // DataTable columns (desktop ≥1024px). Mobile giữ card list bên dưới.
  // Không useMemo([]) — handlers tham chiếu state động (handleMarkImported/form); rebuild rẻ cho 10 cols.
  const tableColumns: Column<ImportOrder>[] = [
    { key: "id", header: "#", sortable: true, render: (o) => <span style={{ fontWeight: 500, cursor: "pointer", color: "var(--accent-blue)" }} onClick={() => setDetailOrder(o)}>#{o.id}</span> },
    {
      key: "supplier_name", header: "NCC", sortable: true,
      render: (o) => {
        if (inlineEdit?.id === o.id && inlineEdit.field === "supplier_name") {
          return renderInlineInput("text");
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span
              style={{ fontWeight: 500, fontSize: 13, cursor: "pointer", textDecoration: "underline", textDecorationColor: "transparent" }}
              onClick={(e) => startInlineEdit(o, "supplier_name", e)}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecorationColor = "currentColor"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecorationColor = "transparent"; }}
              title="Bấm để sửa"
            >
              {o.supplier_name || "—"}
            </span>
            {o.invoice_no && (
              <span
                style={{ fontSize: 11, opacity: 0.6, cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); setDetailOrder(o); }}
                title="Mở chi tiết"
              >
                HĐ {o.invoice_no}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "order_date", header: "Ngày mua", sortable: true,
      render: (o) => {
        if (inlineEdit?.id === o.id && inlineEdit.field === "order_date") {
          return renderInlineInput("date");
        }
        return (
          <span
            style={{ cursor: "pointer" }}
            onClick={(e) => startInlineEdit(o, "order_date", e)}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "underline"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
            title="Bấm để sửa"
          >
            {formatDate(o.order_date)}
          </span>
        );
      },
    },
    { key: "created_at", header: "Ngày tạo", sortable: true, render: (o) => o.created_at ? formatDate(o.created_at) : <span style={{ opacity: 0.4 }}>—</span> },
    {
      key: "received_at", header: "Ngày nhập", sortable: true,
      render: (o) => o.received_at ? formatDate(o.received_at) : <span style={{ opacity: 0.4, fontSize: 12 }}>chưa</span>,
    },
    {
      key: "goods_type", header: "Loại", sortable: true,
      render: (o) => {
        const gt = (o.goods_type || "tieungach") as GoodsType;
        if (inlineEditGoodsId === o.id) {
          // Inline menu: chọn loại mới — click ngoài để hủy.
          return (
            <div style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute", top: "100%", left: 0, zIndex: 30,
                  marginTop: 2, background: "var(--surface, #1b1d24)",
                  border: "1px solid var(--border, #2a2d36)", borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.35)", minWidth: 180, padding: 4,
                }}
              >
                {([["tieungach", "📦 Tiểu ngạch (TQ)"], ["bangke", "📋 Bảng kê 01/TNDN"], ["vat", "🧾 Hóa đơn VAT"]] as const).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className="btn btn-ghost"
                    style={{ display: "block", width: "100%", textAlign: "left", fontSize: 12, padding: "6px 10px", minHeight: 32, opacity: gt === key ? 0.4 : 1 }}
                    disabled={gt === key}
                    onClick={(e) => { e.stopPropagation(); applyInlineGoodsType(o.id, key); }}
                  >
                    {label}{gt === key ? " ✓" : ""}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ display: "block", width: "100%", textAlign: "left", fontSize: 11, color: "var(--text-tertiary)", padding: "4px 10px", minHeight: 28 }}
                  onClick={(e) => { e.stopPropagation(); setInlineEditGoodsId(null); }}
                >
                  Huỷ
                </button>
              </div>
              <Badge variant={gt === "vat" ? "info" : gt === "bangke" ? "warn" : ""}>{GOODS_TYPE_SHORT[gt] || gt} ▾</Badge>
            </div>
          );
        }
        return (
          <Badge
            variant={gt === "vat" ? "info" : gt === "bangke" ? "warn" : ""}
            style={{ cursor: "pointer" }}
          >
            <span
              onClick={(e) => { e.stopPropagation(); setInlineEdit(null); setInlineEditGoodsId(o.id); }}
              title="Bấm để đổi loại đơn"
            >
              {GOODS_TYPE_SHORT[gt] || gt} ▾
            </span>
          </Badge>
        );
      },
    },
    {
      key: "total_amount", header: "Tổng", align: "right", sortable: true,
      render: (o) => {
        if (inlineEdit?.id === o.id && inlineEdit.field === "total_amount") {
          return renderInlineInput("number");
        }
        return (
          <span
            style={{ fontWeight: 500, cursor: "pointer" }}
            onClick={(e) => startInlineEdit(o, "total_amount", e)}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "underline"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
            title="Bấm để sửa"
          >
            {formatVND(o.total_amount)}
          </span>
        );
      },
    },
    {
      key: "paid_amount", header: "Còn nợ", align: "right", sortable: true,
      render: (o) => {
        if (inlineEdit?.id === o.id && inlineEdit.field === "paid_amount") {
          return (
            <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
              <span style={{ fontSize: 10, opacity: 0.65 }}>Đã trả</span>
              {renderInlineInput("number")}
            </div>
          );
        }
        const debt = (o.total_amount || 0) - (o.paid_amount || 0);
        return (
          <span
            style={{ color: debt > 0 ? "var(--accent-red)" : undefined, opacity: debt > 0 ? 1 : 0.4, cursor: "pointer" }}
            onClick={(e) => startInlineEdit(o, "paid_amount", e)}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "underline"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
            title="Bấm để sửa số đã trả"
          >
            {debt > 0 ? formatVND(debt) : "—"}
          </span>
        );
      },
    },
    {
      key: "status", header: "Trạng thái", sortable: true,
      render: (o) => <Badge variant={statusVariant(o.status)}>{statusText(o.status)}</Badge>,
    },
    {
      key: "actions", header: "", align: "right",
      render: (o) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          {o.invoice_file_id && (
            <button className="btn-icon" type="button" onClick={() => viewInvoice(o)} title="Xem HĐ" aria-label="Xem HĐ">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>receipt</span>
            </button>
          )}
          {o.status === "pending" && (
            <button className="btn-icon" type="button" onClick={() => handleMarkImported(o)} disabled={marking || deleting} title="Đã nhập kho" aria-label="Đã nhập kho">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleSubmit = async () => {
    if (!form.supplier_name.trim()) { toast.error("Nhập tên NCC"); return; }
    if (form.total_amount <= 0) { toast.error("Tổng tiền phải > 0"); return; }
    const clientRequestId = saveRequestIdRef.current || (saveRequestIdRef.current = newSaveRequestId());
    setSubmitting(true);
    setSaveConflict(null);
    // Nếu edit draft → PATCH chuyển sang pending.
    const draftEditing = !!editingId && orders.find((o) => o.id === editingId)?.status === "draft";
    try {
    // Build items: total_qty (or qty×per_pack) → quantity = tổng cái; unit_cost = tổng tiền lốc / tổng cái.
    const buildItems = () => {
      const valid = form.items.filter((i) => i.item_name.trim());
      const totalQtyAll = valid.reduce((s, i) => s + retailPieceQty(i), 0);
      const feePerUnit = totalQtyAll > 0 ? (Number(form.extra_fee) || 0) / totalQtyAll : 0;
      return valid.map((i) => {
        const finalQty = retailPieceQty(i);
        const costPerUnit = costPerRetailPiece(i, 0);
        const lineId = i.line_id ?? null;
        const { merge_target: _dropMerge, ...line } = i;
        void _dropMerge;
        const flags = i.merge_target ? { is_vat: i.is_vat, is_bangke: i.is_bangke } : itemFlagsForGoodsType(form.goods_type);
        return {
          ...line, ...flags,
          quantity: Math.round(finalQty),
          retail_split: 1,
          unit_cost: Math.round(costPerUnit + feePerUnit),
          sell_price: i.sell_price ?? null,
          per_pack: null, total_qty: null,
          // Gửi cả id + line_id để backend match import_order_items sau rename
          id: lineId,
          line_id: lineId,
        };
      });
    };
      const payload = {
        order_date: form.order_date,
        supplier_name: form.supplier_name,
        supplier_id: selectedSupplierId ?? undefined,
        supplier_phone: form.supplier_phone || undefined,
        supplier_address: form.supplier_address || undefined,
        supplier_tax_id: form.supplier_tax_id || undefined,
        supplier_bank_account: form.supplier_bank_account || undefined,
        total_amount: form.total_amount,
        paid_amount: form.paid_amount,
        currency: "VND",
        exchange_rate: 1,
        note: form.note || undefined,
        goods_type: form.goods_type,
        vat_rate: form.goods_type === "vat" ? form.vat_rate : 0,
        invoice_no: form.invoice_no || undefined,
        items: buildItems(),
        client_request_id: clientRequestId,
        // Khi sửa draft → chuyển sang pending (đã chốt goods_type).
        ...(editingId && draftEditing ? { status: "pending" as const } : {}),
      } as Parameters<typeof api.importOrders.create>[0];

      let uploadOk = true;
      const fileBase64 = invoiceFile
        ? await new Promise<string>((res, rej) => {
            const fr = new FileReader();
            fr.onload = () => res(String(fr.result).split(",")[1] || "");
            fr.onerror = () => rej(fr.error || new Error("read fail"));
            fr.readAsDataURL(invoiceFile);
          })
        : null;
      let createdId = editingId;
      if (editingId) {
        const res = await api.importOrders.update(editingId, payload as any);
        // SP mới tạo từ đơn imported → báo progress + SKU đã sinh, KHÔNG báo "xong" ngay mà poll verify tồn.
        if (res.created_count && res.created_count > 0) {
          const skus = (res.created_sku || []).map(c => `${c.item_name}=${c.sku}`).join(", ");
          toast.success(`Đã tạo ${res.created_count} SP mới trên POS · SKU: ${skus}. Đang đồng bộ tồn...`);
          // Poll GET /api/products/:id 2 lần (1.5s) confirm in_stock đã !=0 (sync Loyverse đã pull).
          for (const c of res.created_sku || []) {
            try {
              await new Promise(r => setTimeout(r, 1500));
              const p = await api.product(c.item_id);
              const v = p?.variants?.[0];
              const stock = Math.floor(Number(v?.in_stock) || 0);
              if (stock > 0) toast.success(`✓ ${c.item_name} (${c.sku}) · Tồn ${stock}`);
              else toast.warning(`${c.item_name} (${c.sku}) · Tồn chưa đồng bộ (${stock}) — chờ sync 1 phút`);
            } catch { /* poll fail không chặn */ }
          }
          if (res.create_errors?.length) toast.error("Lỗi tạo 1 số SP: " + res.create_errors.slice(0, 2).join("; "));
        } else {
          toast.success("Đã cập nhật đơn nhập");
          if (res.create_errors?.length) toast.error("Lỗi tạo SP mới: " + res.create_errors.slice(0, 2).join("; "));
        }
        // D (2026-07-02): cảnh báo lô đã bán không sửa được SL khi user sửa đơn imported.
        if (res.stock_lot_warnings?.length) {
          toast.warning("Lô đã bán — không sửa SL: " + res.stock_lot_warnings.join("; "));
        }
      } else {
        const created = await api.importOrders.create(payload);
        createdId = created?.id;
        toast.success("Đã tạo đơn nhập");
      }
      if (invoiceFile && createdId && fileBase64) {
        try {
          const mt = invoiceFile.type || (invoiceFile.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "application/octet-stream");
          await api.importOrders.attachments.upload(createdId, { file_name: invoiceFile.name, base64: fileBase64, mime_type: mt });
        } catch (e: unknown) {
          const msg = (e as Error)?.message || "Lỗi không rõ";
          toast.error("Đã lưu nhưng upload chứng từ lỗi: " + msg);
          uploadOk = false;
        }
      }
      clearBulkSelect();
      clearSaveRequestId();
      setShowModal(false);
      setEditingId(null);
      setForm(emptyForm(defaultGoodsType));
      if (uploadOk) setInvoiceFile(null);
      resetScanUi();
      await reloadAfterMutation();
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 409 && e.payload && typeof e.payload === "object") {
        const lines = formatImportConflict(e.payload as ImportOrderConflict, form.items);
        setSaveConflict(lines);
        toast.error(lines[0] || "Không lưu được đơn đã gộp");
      } else if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        clearSaveRequestId();
        toast.error((e as Error).message || "Lỗi lưu đơn nhập");
      } else {
        toast.error((e as Error).message || "Lỗi lưu đơn nhập");
      }
    } finally {
      setSubmitting(false);
    }
  };

  /** Dòng is_new chưa có mapped_item_id — receive lần 1 có thể skip nếu partial. */
  const unmappedNewItems = (order: ImportOrder) =>
    (order.items || []).filter((it) => !!it.is_new_product && !it.mapped_item_id);

  type ReceiveResult = ImportOrderReceiveResult;

  /**
   * Toast receive + trả về phần kết luận để nơi gọi tự xác minh trạng thái server.
   * deferVerdict: không báo "đã nhập kho"/"đã thanh toán" cho tới khi đọc lại thấy status=imported.
   */
  const reportReceiveResult = async (
    order: ImportOrder,
    r: ReceiveResult,
    opts: { deferVerdict?: boolean } = {},
  ) => {
    // Prefer server remaining_new (post-write truth). Stale local order only if field missing.
    const remaining = Array.isArray(r.remaining_new)
      ? r.remaining_new
      : r.httpStatus === 200 ? unmappedNewItems(order).map((it) => it.item_name).filter(Boolean) : [];
    const remainingCount = typeof r.remaining_count === "number" && Number.isSafeInteger(r.remaining_count) && r.remaining_count >= 0
      ? r.remaining_count : r.httpStatus === 200 ? remaining.length : null;
    const incomplete = r.httpStatus !== 200 || r.ok !== true || !!r.incomplete || (remainingCount !== null && remainingCount > 0);
    const parts: string[] = [];
    if (r.added?.length) parts.push(`cộng tồn ${r.added.length} SP`);
    if (r.created?.length) parts.push(`tạo mới ${r.created.length} SP`);
    if (r.skipped?.length) parts.push(`bỏ qua ${r.skipped.length}`);
    if (!opts.deferVerdict && incomplete) {
      const count = remainingCount === null ? "chưa xác định số dòng còn lại" : `còn ${remainingCount} dòng chưa hoàn tất`;
      toast.warning(`Nhập kho chưa hoàn tất: ${count}. Kiểm tra lại đơn trước khi thao tác tiếp.`);
    } else if (!opts.deferVerdict && !incomplete) {
      toast.success("Đã nhập kho: " + (parts.join(", ") || "xong"));
    }
    // Cảnh báo phụ (vd nhiều giá vốn cùng đơn → giữ giá vốn SP hiện tại, từng lô giữ giá riêng).
    // Chỉ hiện khi receive trọn vẹn; không biến thành lỗi/partial và không đổi kết luận thành công.
    // deferVerdict → trả về cho nơi gọi hiển thị sau khi đọc lại xác nhận imported.
    const warningText = receiveWarningForResult({
      ok: r.ok,
      incomplete: r.incomplete,
      remainingCount: remainingCount ?? 0,
      warnings: r.warnings,
    });
    if (warningText && !opts.deferVerdict) toast.warning(warningText);
    if (r.skipped?.length) {
      const names = r.skipped.slice(0, 6).join("; ");
      toast.warning(
        `Bỏ qua ${r.skipped.length} dòng: ${names}${r.skipped.length > 6 ? "…" : ""}`,
      );
    }
    // Chỉ báo khi server thực sự ghi nhận thanh toán trong lần receive này.
    if (!opts.deferVerdict && !incomplete && r.paid_amount_applied != null && r.paid_amount_applied > 0) {
      toast.success(`Đã ghi nhận thanh toán ${formatVND(r.paid_amount_applied)} (ghi sổ, không tạo giao dịch tiền mặt)`);
    }
    if (!incomplete && r.created_sku?.length) {
      const skus = r.created_sku.map((c) => `${c.item_name}=${c.sku}`).join(", ");
      toast.success(`SP mới đã tạo · SKU: ${skus}. Đang đồng bộ tồn...`);
      for (const c of r.created_sku) {
        try {
          await new Promise((rr) => setTimeout(rr, 1500));
          const p = await api.product(c.item_id);
          const v = p?.variants?.[0];
          const stock = Math.floor(Number(v?.in_stock) || 0);
          if (stock > 0) toast.success(`✓ ${c.item_name} (${c.sku}) · Tồn ${stock}`);
          else toast.warning(`${c.item_name} (${c.sku}) · Tồn chưa đồng bộ (${stock}) — chờ sync 1 phút`);
        } catch { /* poll fail không chặn */ }
      }
    }
    if (r.errors?.length) toast.error("Lỗi: " + r.errors.slice(0, 2).join("; "));
    return { incomplete, remaining, remainingCount, parts, warningText };
  };

  /** Reload list + refresh detailOrder nếu còn mở. Trả về bản ghi mới để xác nhận trạng thái thật. */
  const reloadOrdersAndDetail = async (keepDetailId?: number | null) => {
    const r = await api.importOrders.list({
      status: statusFilter || undefined,
      from: dateFrom || undefined,
      to: dateTo || undefined,
      q: searchDebounced || undefined,
    });
    const next = r.orders || [];
    setOrders(next);
    let fresh: ImportOrder | null = null;
    if (keepDetailId != null) {
      fresh = next.find((o) => o.id === keepDetailId) || null;
      setDetailOrder(fresh);
    }
    void loadAnalytics();
    return fresh;
  };

  const handleMarkImported = async (order: ImportOrder) => {
    if (markingRef.current) return;
    markingRef.current = true;
    setMarking(true);
    try {
      // Đơn CŨ (cách hôm nay VN > 7 ngày) → phân biệt "chỉ số liệu" vs "đẩy tồn POS".
      const isOld = isOldImportOrder(order.order_date);
      // ponytail: message-1 động theo isOld — đơn cũ không hứa "đẩy Loyverse" (sẽ hỏi book_only ở popup-2).
      // Thanh toán: backend chỉ tự chốt "trả đủ" khi đơn pending nhập kho lần đầu VÀ paid_amount hiện tại = 0.
      // Đơn đã ghi nhận trả một phần → giữ nguyên, không tự trả phần còn lại.
      const paidNotice = (order.paid_amount || 0) > 0
        ? `Thanh toán: đơn đã ghi nhận trả ${formatVND(order.paid_amount)} — GIỮ NGUYÊN, không tự thanh toán phần còn lại.`
        : "Thanh toán: lần nhập đầu tiên và chưa ghi nhận trả trước đó → mặc định đánh dấu ĐÃ THANH TOÁN ĐỦ (ghi sổ, không tạo giao dịch tiền mặt). Đơn cũ chọn \"Chỉ số liệu\" thì KHÔNG tự thanh toán.";
      const msg1 = isOld
        ? `Nhập kho đơn #${order.id}? Đơn cũ (cách đây hơn ${OLD_IMPORT_DAYS} ngày) — bước kế sẽ hỏi bạn "chỉ số liệu" hay "đẩy tồn POS".\n${paidNotice}`
        : `Nhập kho đơn #${order.id}? Hệ thống sẽ cộng tồn cho SP đã map, tạo SP mới cho dòng đánh dấu "hàng mới", và đẩy lên Loyverse.\n${paidNotice}`;
      if (!await confirm({ title: "Nhập kho", message: msg1, confirmLabel: "Nhập kho" })) return;

      let bookOnly = false;
      if (isOld) {
        const ok = await confirm({
          title: "Đơn nhập cũ",
          message: `Đơn ngày ${order.order_date?.slice(0, 10)} (cách đây hơn ${OLD_IMPORT_DAYS} ngày) — có vẻ là đơn CŨ đã bán.\n\n• "Chỉ số liệu": ghi sổ thuế/COGS, tồn POS GIỮ NGUYÊN (Loyverse đã tự trừ khi bán).\n• "Đẩy tồn POS": đè số tồn lên POS (chỉ khi hàng thật chưa bán).`,
          confirmLabel: "Chỉ số liệu",
          cancelLabel: "Đẩy tồn POS",
          danger: false,
        });
        bookOnly = ok; // confirm=true → chỉ số liệu; cancel=false → đẩy tồn
      }

      let r: ReceiveResult | null = null;
      let receiveError: string | null = null;
      try {
        r = await api.importOrders.receive(order.id, bookOnly);
      } catch (e: unknown) {
        if (e instanceof ReceiveCheckpointError) r = e.checkpoint;
        receiveError = e instanceof Error ? e.message : "Lỗi nhập kho";
      }
      // Chưa chốt "đã nhập/đã trả" — receive có thể ok=true nhưng thua CAS, hoặc request lỗi
      // sau khi server đã ghi một phần. Chỉ báo kết luận sau khi đọc lại thấy status=imported.
      const result = r ? await reportReceiveResult(order, r, { deferVerdict: true }) : null;

      let fresh: ImportOrder | null = null;
      let refreshError: string | null = null;
      try {
        fresh = await reloadOrdersAndDetail(order.id);
      } catch (e: unknown) {
        refreshError = (e as Error)?.message || "Không tải lại được đơn";
      }

      const outcome = classifyReceiveOutcome({
        receiveError,
        refreshError,
        // Không có HTTP 200 cuối cùng thì không thể kết luận đã nhập xong.
        incomplete: !r || (result?.incomplete === true && (r.httpStatus !== 200 || (result.remainingCount ?? 0) > 0)),
        freshStatus: r?.httpStatus === 200 && r.ok === true && !result?.incomplete ? fresh?.status ?? null : null,
      });

      const checkpointNote = receiveError && r?.httpStatus === 202 && result?.remainingCount != null
        ? ` Trước yêu cầu lỗi, checkpoint cuối còn ${result.remainingCount} dòng.` : "";
      if (outcome === "unverified-refresh") {
        toast.warning(
          `Đã gửi yêu cầu nhập kho nhưng KHÔNG đọc lại được trạng thái (${refreshError}).${checkpointNote} Chưa xác minh — kiểm tra lại đơn trước khi thao tác tiếp.`,
        );
      } else if (outcome === "unverified-request") {
        // Request lỗi ≠ chắc chắn chưa ghi: server có thể đã cập nhật một phần. Không khẳng định rollback.
        toast.error(
          `Yêu cầu nhập kho lỗi: ${receiveError}.${checkpointNote} Trạng thái đơn CHƯA xác minh — kiểm tra lại trước khi thao tác tiếp.`,
        );
      } else if (outcome === "success") {
        toast.success("Đã nhập kho: " + (result?.parts.join(", ") || "xong"));
        // Cảnh báo phụ từ server (giá vốn giữ nguyên do nhiều giá trong cùng đơn) — chỉ thông tin.
        if (result?.warningText) toast.warning(result.warningText);
        if ((r?.paid_amount_applied || 0) > 0) {
          toast.success(`Đã ghi nhận thanh toán ${formatVND(r!.paid_amount_applied!)} (ghi sổ, không tạo giao dịch tiền mặt)`);
        }
        setDetailOrder(null);
      } else if (outcome === "incomplete") {
        // Đơn còn dòng chưa ghi lô (vd dòng chưa map/hàng mới chưa tạo) → pending là ĐÚNG.
        const names = result?.remaining.slice(0, 5).join("; ") || "";
        const count = result?.remainingCount == null ? "chưa xác định số dòng còn lại" : `còn ${result.remainingCount} dòng chưa hoàn tất`;
        toast.warning(
          `Nhập kho chưa hoàn tất: ${count}${names ? ` — ${names}` : ""}. Kiểm tra lại đơn trước khi thao tác tiếp.`,
        );
        setDetailOrder((prev) => prev ?? order);
      } else if (outcome === "conflict") {
        toast.warning(
          fresh
            ? "Server chưa chốt trạng thái đơn (có thể đang được cập nhật đồng thời) — đã làm mới, bấm Nhập kho lại nếu cần."
            : "Chưa xác minh được trạng thái đơn sau khi nhập (đơn không có trong danh sách đang lọc) — kiểm tra lại.",
        );
        // Giữ modal mở khi pending/conflict (kể cả khi đơn rơi khỏi danh sách đang lọc).
        setDetailOrder((prev) => prev ?? order);
      }
    } finally {
      markingRef.current = false;
      setMarking(false);
    }
  };

  /** Đơn imported còn is_new + !mapped → re-receive idempotent (BE skip line đã lô). */
  const handleCreateRemainingProducts = async (order: ImportOrder) => {
    if (markingRef.current) return;
    markingRef.current = true;
    setMarking(true);
    try {
      const pending = unmappedNewItems(order);
      if (!pending.length) {
        toast.success("Không còn SP hàng mới chưa tạo");
        return;
      }
      const names = pending.map((it) => it.item_name).filter(Boolean).slice(0, 5).join(", ");
      if (!await confirm({
        title: "Tạo SP còn lại",
        message: `Tạo ${pending.length} SP còn lại trên POS?\n${names}${pending.length > 5 ? "…" : ""}\n\nDòng đã nhập lô sẽ được bỏ qua (idempotent).`,
        confirmLabel: `Tạo ${pending.length} SP`,
      })) return;

      // Tạo SP còn lại luôn đẩy tồn cho SP mới (bookOnly=false).
      const bookOnly = false;
      const r = await api.importOrders.receive(order.id, bookOnly);
      await reportReceiveResult(order, r);
      await reloadOrdersAndDetail(order.id);
    } catch (e: unknown) {
      const checkpointNote = e instanceof ReceiveCheckpointError
        ? ` Trước yêu cầu lỗi, checkpoint cuối còn ${e.checkpoint.remaining_count} dòng.` : "";
      toast.error(`${e instanceof Error ? e.message : "Lỗi tạo SP còn lại"}.${checkpointNote} Trạng thái hiện tại chưa xác minh — kiểm tra lại đơn.`);
    } finally {
      markingRef.current = false;
      setMarking(false);
    }
  };

  const handleDeleteOrder = async (order: ImportOrder) => {
    if (!await confirm({
      title: "Xóa đơn nhập",
      message: `Xóa đơn nhập #${order.id}${order.supplier_name ? ` (${order.supplier_name})` : ""}?\n\nSẽ gỡ cả: dòng SP, công nợ NCC, và VAT/bảng kê đã gắn cho đơn đã bán (cập nhật lại sổ S1A). Không thể hoàn tác.`,
      danger: true,
      confirmLabel: "Xóa đơn",
    })) return;
    setDeleting(true);
    try {
      const r = await api.importOrders.remove(order.id);
      toast.success(r.s1a_regen_dates?.length ? `Đã xóa đơn · cập nhật S1A ${r.s1a_regen_dates.length} ngày` : "Đã xóa đơn nhập");
      setDetailOrder(null);
      await reloadAfterMutation();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi xóa đơn");
    } finally {
      setDeleting(false);
    }
  };

  const handleSearch = async (query: string, idx: number) => {
    setActiveItemIdx(idx);
    if (query.length < 2) { setSearchResults([]); return; }
    try {
      const items = (await searchCatalog(query, 8)) ?? (await api.items.search(query)).items;
      setSearchResults((items || []).slice(0, 8).map((p) => ({ id: p.id, item_name: p.item_name, sku: p.sku, cost: p.cost, default_price: p.default_price })));
    } catch { setSearchResults([]); }
  };

  const handleMapSearch = async (query: string, idx: number) => {
    if (query.length < 2) { setMapResults((m) => ({ ...m, [idx]: [] })); return; }
    try {
      const items = (await searchCatalog(query, 6)) ?? (await api.items.search(query)).items;
      setMapResults((m) => ({ ...m, [idx]: (items || []).slice(0, 6).map((p) => ({ id: p.id, item_name: p.item_name, sku: p.sku, default_price: p.default_price })) }));
    } catch { setMapResults((m) => ({ ...m, [idx]: [] })); }
  };

  // Lưu map mới cho 1 dòng trong đơn đã có. Map SP cũ → đổi luôn tên dòng theo SP POS.
  const handleSaveDetailMap = async (itemId: number, newMappedId: string | null, newMappedName?: string) => {
    if (!detailOrder) return;
    try {
      const updatedItems = (detailOrder.items || []).map((it) =>
        it.id === itemId ? { ...it, mapped_item_id: newMappedId } : it
      );
      await api.importOrders.update(detailOrder.id, { items: updatedItems as any });
      toast.success("Đã cập nhật map");
      setDetailOrder({ ...detailOrder, items: updatedItems as any });
      void loadAnalytics();
      loadOrders();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi cập nhật map");
    }
  };

  // Đơn đã lưu: đánh dấu/bỏ 1 dòng là "hàng mới" (khi nhập kho sẽ tạo SP mới).
  const handleToggleNew = async (itemId: number, isNew: boolean) => {
    if (!detailOrder) return;
    try {
      const updatedItems = (detailOrder.items || []).map((it) =>
        it.id === itemId ? { ...it, is_new_product: isNew, mapped_item_id: isNew ? null : it.mapped_item_id } : it
      );
      await api.importOrders.update(detailOrder.id, { items: updatedItems as any });
      toast.success(isNew ? "Đã đánh dấu hàng mới" : "Đã bỏ đánh dấu");
      setDetailOrder({ ...detailOrder, items: updatedItems as any });
      void loadAnalytics();
      loadOrders();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi cập nhật");
    }
  };

  // Lưu header đơn trực tiếp trong detail modal (PATCH partial, KHÔNG gửi items).
  const handleSaveDetailHeader = async () => {
    if (!detailOrder || detailSaving) return;
    if (!detailEdit.supplier_name.trim()) { toast.error("Tên NCC không được để trống"); return; }
    if (detailEdit.total_amount <= 0) { toast.error("Tổng tiền phải > 0"); return; }
    setDetailSaving(true);
    try {
      await api.importOrders.update(detailOrder.id, {
        supplier_name: detailEdit.supplier_name.trim(),
        supplier_phone: detailEdit.supplier_phone,
        supplier_address: detailEdit.supplier_address,
        supplier_tax_id: detailEdit.supplier_tax_id,
        supplier_bank_account: detailEdit.supplier_bank_account,
        order_date: detailEdit.order_date,
        total_amount: detailEdit.total_amount,
        paid_amount: detailEdit.paid_amount,
        note: detailEdit.note,
        invoice_no: detailEdit.invoice_no,
      } as any);
      toast.success("Đã cập nhật đơn");
      setDetailOrder({ ...detailOrder, ...detailEdit });
      await reloadAfterMutation();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi cập nhật đơn");
    } finally {
      setDetailSaving(false);
    }
  };

  // Chọn NCC chuẩn trong detail modal → fill thông tin (user vẫn bấm Lưu đơn để PATCH).
  const pickDetailSupplier = (s: Supplier) => {
    setDetailEdit((prev) => ({
      ...prev,
      supplier_name: s.name,
      supplier_phone: s.phone || prev.supplier_phone,
      supplier_address: s.address || prev.supplier_address,
      supplier_tax_id: s.tax_id || prev.supplier_tax_id,
      supplier_bank_account: s.bank_account || prev.supplier_bank_account,
    }));
  };

  // ── Tách dòng SP: modal nhập N phần, sum qty = qty gốc.
  const [splitItem, setSplitItem] = useState<ImportOrderItem | null>(null);
  const [splitParts, setSplitParts] = useState<Array<{ quantity: string }>>([]);
  const [splitSubmitting, setSplitSubmitting] = useState(false);

  const openSplit = (it: ImportOrderItem) => {
    if (it.id == null) return;
    setSplitItem(it);
    // Mặc định 2 phần: qty = 0.
    setSplitParts([{ quantity: "" }, { quantity: "" }]);
  };

  const splitSum = splitParts.reduce((s, p) => s + (Number(p.quantity) || 0), 0);
  const splitTargetQty = splitItem?.quantity ?? 0;
  const splitValid = splitParts.length > 1 && splitSum === splitTargetQty && splitParts.every((p) => (Number(p.quantity) || 0) > 0);

  const handleSplit = async () => {
    if (!splitItem || splitItem.id == null || !detailOrder) return;
    if (!splitValid) { toast.error("Tổng số lượng các phần phải bằng số lượng dòng gốc và mỗi phần > 0"); return; }
    setSplitSubmitting(true);
    try {
      await api.importOrders.splitLine(detailOrder.id, splitItem.id, splitParts.map((p) => ({ quantity: Number(p.quantity) })));
      toast.success("Đã tách dòng");
      setSplitItem(null);
      await reloadOrdersAndDetail(detailOrder.id);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Không tách được dòng (có thể đã nhập kho)");
    } finally {
      setSplitSubmitting(false);
    }
  };

  // Đánh dấu đã trả đủ cho đơn → set paid_amount = total, backend sync xuống payable.
  const handleMarkPaid = async (order: ImportOrder) => {
    const remaining = order.total_amount - order.paid_amount;
    if (remaining <= 0) return;
    if (!await confirm({ title: "Đánh dấu đã trả đủ", message: `Ghi nhận đã trả đủ ${formatVND(remaining)} cho ${order.supplier_name || "NCC"}?\nĐồng bộ sang màn Công nợ.`, confirmLabel: "Đã trả đủ" })) return;
    try {
      await api.importOrders.update(order.id, { paid_amount: order.total_amount } as any);
      toast.success("Đã ghi nhận trả đủ");
      setDetailOrder({ ...detailOrder!, paid_amount: order.total_amount });
      setDetailEdit({ ...detailEdit, paid_amount: order.total_amount });
      await reloadAfterMutation();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi cập nhật thanh toán");
    }
  };

  // Đổi loại đơn nhập ngay tại dòng (inline edit goods_type trong DataTable).
  const [inlineEditGoodsId, setInlineEditGoodsId] = useState<number | null>(null);
  // Inline cell edit: order_date | total_amount | paid_amount | supplier_name
  const [inlineEdit, setInlineEdit] = useState<{ id: number; field: string } | null>(null);
  const [inlineDraft, setInlineDraft] = useState<string>("");
  const applyInlineGoodsType = async (orderId: number, newType: GoodsType) => {
    setInlineEditGoodsId(null);
    try {
      const res = await api.importOrders.update(orderId, { goods_type: newType } as any);
      // Update local state thay vì reload full (giữ scroll position).
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, goods_type: newType } : o));
      if (detailOrder?.id === orderId) setDetailOrder((d) => d ? { ...d, goods_type: newType } : d);
      const labels: Record<string, string> = { tieungach: "Tiểu ngạch (TQ)", vn: "Hàng VN", bangke: "Bảng kê 01/TNDN", vat: "Hóa đơn VAT" };
      toast.success(`Đã đổi #${orderId} → ${labels[newType] || newType}`);
      if (res.stock_lot_warnings?.length) toast.warning(res.stock_lot_warnings.join("; "));
      void loadAnalytics();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi đổi loại đơn");
    }
  };

  const startInlineEdit = (o: ImportOrder, field: "order_date" | "total_amount" | "paid_amount" | "supplier_name", e?: React.MouseEvent) => {
    e?.stopPropagation();
    setInlineEditGoodsId(null);
    let draft = "";
    if (field === "order_date") draft = (o.order_date || "").slice(0, 10);
    else if (field === "total_amount") draft = String(Number(o.total_amount) || 0);
    else if (field === "paid_amount") draft = String(Number(o.paid_amount) || 0);
    else draft = o.supplier_name || "";
    setInlineEdit({ id: o.id, field });
    setInlineDraft(draft);
  };

  const cancelInlineEdit = () => {
    setInlineEdit(null);
    setInlineDraft("");
  };

  const saveInlineEdit = async () => {
    if (!inlineEdit) return;
    const { id, field } = inlineEdit;
    const order = orders.find((o) => o.id === id);
    if (!order) { cancelInlineEdit(); return; }

    let payload: Record<string, string | number> | null = null;
    let localPatch: Partial<ImportOrder> = {};

    if (field === "order_date") {
      const next = inlineDraft.trim().slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(next)) { toast.error("Ngày mua không hợp lệ"); return; }
      if (next === (order.order_date || "").slice(0, 10)) { cancelInlineEdit(); return; }
      payload = { order_date: next };
      localPatch = { order_date: next };
    } else if (field === "total_amount") {
      const next = Number(inlineDraft);
      if (!Number.isFinite(next) || next < 0) { toast.error("Tổng tiền không hợp lệ"); return; }
      if (next === Number(order.total_amount)) { cancelInlineEdit(); return; }
      payload = { total_amount: next };
      localPatch = { total_amount: next };
    } else if (field === "paid_amount") {
      const next = Number(inlineDraft);
      if (!Number.isFinite(next) || next < 0) { toast.error("Số đã trả không hợp lệ"); return; }
      if (next === Number(order.paid_amount)) { cancelInlineEdit(); return; }
      payload = { paid_amount: next };
      localPatch = { paid_amount: next };
    } else if (field === "supplier_name") {
      const next = inlineDraft.trim();
      if (!next) { toast.error("Tên NCC không được trống"); return; }
      if (next === (order.supplier_name || "").trim()) { cancelInlineEdit(); return; }
      payload = { supplier_name: next };
      localPatch = { supplier_name: next };
    }

    if (!payload) { cancelInlineEdit(); return; }

    // Clear early so Enter+blur cannot double-PATCH.
    setInlineEdit(null);
    setInlineDraft("");
    try {
      await api.importOrders.update(id, payload as any);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, ...localPatch } : o)));
      if (detailOrder?.id === id) {
        setDetailOrder((d) => (d ? { ...d, ...localPatch } : d));
        setDetailEdit((prev) => ({ ...prev, ...localPatch } as typeof prev));
      }
      toast.success(`Đã cập nhật #${id}`);
      void loadAnalytics();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi cập nhật");
    }
  };

  const renderInlineInput = (type: "date" | "number" | "text") => (
    <input
      type={type}
      autoFocus
      value={inlineDraft}
      onChange={(e) => setInlineDraft(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onBlur={() => { void saveInlineEdit(); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); void saveInlineEdit(); }
        if (e.key === "Escape") { e.preventDefault(); cancelInlineEdit(); }
      }}
      style={{
        width: type === "date" ? 140 : type === "number" ? 110 : "100%",
        minWidth: type === "text" ? 120 : undefined,
        fontSize: 13,
        padding: "4px 6px",
        borderRadius: 6,
        border: "1px solid var(--accent-blue, #3b82f6)",
        background: "var(--surface, #1b1d24)",
        color: "inherit",
      }}
      aria-label={
        type === "date" ? "Sửa ngày mua"
          : type === "number" ? "Sửa số tiền"
          : "Sửa tên NCC"
      }
    />
  );

  // Đổi loại đơn trong detail modal (tiểu ngạch ↔ bảng kê ↔ VAT) — không cần xóa tạo lại.
  const [showGoodsPicker, setShowGoodsPicker] = useState(false);
  const handleChangeGoodsType = async (order: ImportOrder) => { setShowGoodsPicker(true); };
  const applyGoodsType = async (newType: string) => {
    if (!detailOrder) return;
    await applyInlineGoodsType(detailOrder.id, newType as GoodsType);
    setShowGoodsPicker(false);
  };

  // Đính hóa đơn VAT ĐẦU VÀO vào đơn nhập — chỉ ghi nhận để đối chiếu/khấu trừ, KHÔNG đụng cờ thuế SP.
  const handleAttachVat = async (order: ImportOrder) => {
    const invoiceNo = window.prompt("Nhập SỐ HÓA ĐƠN VAT đầu vào (để trống nếu chưa có):", order.invoice_no || "");
    if (invoiceNo === null) return; // huỷ
    const rateStr = window.prompt("% VAT (mặc định 8):", String(order.vat_rate || 8));
    if (rateStr === null) return;
    if (!await confirm({ title: "Đính hóa đơn VAT đầu vào", message: `Ghi nhận hóa đơn VAT đầu vào cho đơn #${order.id}?\nChỉ lưu số HĐ + %VAT để đối chiếu/khấu trừ. KHÔNG tính VAT đầu ra khi bán (tránh thuế chồng thuế).`, confirmLabel: "Lưu HĐ VAT" })) return;
    try {
      await api.importOrders.attachVat(order.id, { invoice_no: invoiceNo || undefined, vat_rate: Number(rateStr) || 8 });
      toast.success("Đã ghi nhận hóa đơn VAT đầu vào");
      setDetailOrder(null);
      await reloadAfterMutation();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi đính VAT");
    }
  };

  // AI gợi giá bán cho TẤT CẢ dòng — tính theo giá vốn/cái thực (đã chia total_qty + phí ship).
  const suggestPriceAll = async () => {
    const tq = form.items.reduce((s, i) => s + retailPieceQty(i), 0);
    const feePerUnit = tq > 0 ? (Number(form.extra_fee) || 0) / tq : 0;
    const targets = form.items.map((it, idx) => ({ it, idx })).filter(({ it }) => it.item_name.trim() && it.unit_cost > 0);
    if (targets.length === 0) { toast.error("Chưa có dòng SP hợp lệ"); return; }
    setAiPricingIdx(-2);
    let ok = 0;
    try {
      for (const { it, idx } of targets) {
        const costPerRetail = Math.round(costPerRetailPiece(it, feePerUnit));
        try {
          const r = await api.ai.suggestPrice({ item_name: it.item_name.trim(), cost: costPerRetail, category: form.goods_type });
          if (r.suggested_price && r.suggested_price > 0) { updateFormItem(idx, "sell_price", r.suggested_price); ok++; }
        } catch { /* bỏ qua dòng lỗi, tiếp dòng sau */ }
      }
      toast.success(`AI đã gợi giá ${ok}/${targets.length} dòng`);
    } finally {
      setAiPricingIdx(null);
    }
  };

  const mergedOrder = form.items.some((it) => !!it.merge_target);

  const updateFormItem = (idx: number, field: keyof OrderFormItem, value: string | number | boolean | null) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      // KHÔNG tự ghi đè total_amount = Σ(qty×unit_cost): unit_cost đã gồm phí ship chia đều,
      // còn total là tiền hàng NCC báo (đã gồm ship). Suy ngược sẽ cộng ship 2 lần → phình tiền.
      return { ...prev, items };
    });
  };

  const analyticsScopeLabel = analyticsRange.mode === "all"
    ? "tất cả thời gian"
    : analyticsRange.from && analyticsRange.to
      ? `${analyticsRange.from.slice(5)} → ${analyticsRange.to.slice(5)}`
      : analyticsRange.from
        ? `từ ${analyticsRange.from.slice(5)}`
        : analyticsRange.to
          ? `đến ${analyticsRange.to.slice(5)}`
          : "tất cả thời gian";

  // Dashboard: gom trend dài kỳ theo tháng để không vỡ layout.
  // ponytail: ngưỡng 62 điểm — đủ cho 2 tháng lẻ; dài hơn thì gộp tháng.
  // key tách khỏi label: label MM-DD trùng nhau qua các năm, key phải là ngày/tháng đầy đủ.
  const trendBuckets = useMemo(() => {
    if (!analytics) return [] as TrendBucket[];
    if (analytics.trend.length > 62) return groupTrendByMonth(analytics.trend);
    return bucketTrendByDay(analytics.trend);
  }, [analytics]);

  const trendMax = useMemo(
    () => trendBuckets.reduce((m, b) => Math.max(m, b.invoice_value), 0),
    [trendBuckets],
  );
  const supplierMax = useMemo(
    () => (analytics?.suppliers || []).reduce((m, s) => Math.max(m, s.invoice_value), 0),
    [analytics],
  );
  const goodsMax = useMemo(
    () => (analytics?.goods_types || []).reduce((m, g) => Math.max(m, g.invoice_value), 0),
    [analytics],
  );

  return (
    <>
      <GlassPanel
        title="Đã nhập · theo ngày mua"
        badge={analyticsScopeLabel}
        badgeVariant="info"
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }} role="group" aria-label="Khoảng thời gian dashboard">
          {([
            { key: "month", label: "Tháng này" },
            { key: "prev", label: "Tháng trước" },
            { key: "all", label: "Tất cả" },
            { key: "custom", label: "Tùy chọn" },
          ] as const).map((p) => (
            <button
              key={p.key}
              type="button"
              className={`btn btn-compact ${analyticsRange.mode === p.key ? "" : "btn-ghost"}`}
              aria-pressed={analyticsRange.mode === p.key}
              style={analyticsRange.mode === p.key
                ? { background: "var(--accent-blue-bg)", color: "var(--accent)", border: "1px solid var(--accent-blue-muted)", fontWeight: 600 }
                : undefined}
              onClick={() => {
                if (p.key === "month") setAnalyticsRange({ mode: "month", ...currentMonthRange() });
                else if (p.key === "prev") setAnalyticsRange({ mode: "prev", ...previousMonthRange() });
                else if (p.key === "all") setAnalyticsRange({ mode: "all", from: "", to: "" });
                else setAnalyticsRange({ mode: "custom", from: dateFrom, to: dateTo });
              }}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className="btn btn-ghost btn-compact"
            onClick={() => void loadAnalytics()}
            disabled={analyticsLoading}
            aria-label="Tải lại số liệu dashboard"
            title="Tải lại số liệu"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>refresh</span>
          </button>
          {analyticsLoading && analytics && (
            <span style={{ alignSelf: "center", fontSize: 12, color: "var(--text-tertiary)" }} role="status">Đang cập nhật số liệu…</span>
          )}
        </div>
        {analyticsRange.mode === "custom" && (
          <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 12 }}>
            Khoảng tùy chọn dùng chung ô ngày của danh sách bên dưới. Bỏ trống cả hai = tất cả thời gian.
          </p>
        )}

        {analyticsLoading && !analytics ? (
          <div className="kpi-grid" aria-busy="true">
            <SkeletonKpi /><SkeletonKpi /><SkeletonKpi /><SkeletonKpi />
          </div>
        ) : analyticsError ? (
          <ErrorState error={analyticsError} onRetry={() => void loadAnalytics()} message="Không tải được số liệu nhập hàng" />
        ) : !analytics || analytics.summary.order_count + analytics.summary.pending_count === 0 ? (
          <EmptyState icon="inventory_2" title="Chưa có đơn nhập trong kỳ" description="Thử chọn khoảng thời gian khác." />
        ) : (
          <>
            <div className="kpi-grid">
              <KpiCard label="Giá trị nhập" value={formatVND(analytics.summary.invoice_value)} note={`${analytics.summary.order_count} đơn đã nhập`} dotColor="blue" />
              <KpiCard label="Đơn đã nhập" value={analytics.summary.order_count.toString()} note="theo ngày mua" dotColor="ok" />
              <KpiCard
                label="Đã thanh toán"
                value={formatVND(analytics.summary.paid_value)}
                note={`Còn nợ ${formatVND(analytics.summary.remaining_value)}`}
                dotColor={analytics.summary.remaining_value > 0 ? "warn" : "ok"}
              />
              <KpiCard label="Chờ nhập" value={analytics.summary.pending_count.toString()} note="đơn chưa nhập kho" dotColor="warn" />
            </div>
            <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: -6, marginBottom: 12 }}>
              Số liệu toàn bộ đơn đã nhập theo ngày mua, không phụ thuộc bộ lọc bên dưới. “Đã thanh toán” là ghi nhận sổ sách, không phải giao dịch tiền mặt.
            </p>

            {trendBuckets.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Giá trị nhập theo {trendBuckets[0].key.length === 7 ? "tháng" : "ngày"}</h4>
                <div
                  role="img"
                  aria-label={`Biểu đồ giá trị nhập theo ${trendBuckets[0].key.length === 7 ? "tháng" : "ngày"}: ${trendBuckets.length} mốc, cao nhất ${formatVND(trendMax)}. Chi tiết từng mốc ở danh sách ẩn bên dưới.`}
                  style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 90, overflowX: "auto", paddingBottom: 2 }}
                >
                  {trendBuckets.map((b) => (
                    <div
                      key={b.key}
                      aria-hidden="true"
                      title={`${b.label}: ${formatVND(b.invoice_value)} · ${b.order_count} đơn`}
                      style={{ flex: "1 0 8px", maxWidth: 42, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}
                    >
                      <div style={{ height: `${trendMax > 0 ? Math.max(2, (b.invoice_value / trendMax) * 100) : 2}%`, background: "var(--accent)", borderRadius: 3, minHeight: 2 }} />
                    </div>
                  ))}
                </div>
                <ul className="sr-only">
                  {trendBuckets.map((b) => (
                    <li key={b.key}>{b.label}: {formatVND(b.invoice_value)}, {b.order_count} đơn</li>
                  ))}
                </ul>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>
                  <span>{trendBuckets[0].label}</span>
                  <span>{trendBuckets[trendBuckets.length - 1].label}</span>
                </div>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
              {analytics.suppliers.length > 0 && (
                <div>
                  <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Top NCC theo giá trị</h4>
                  <div className="cat-bars">
                    {analytics.suppliers.map((s) => (
                      <div className="cat-row" key={s.supplier_name} style={{ cursor: "default" }}>
                        <div className="cat-row-head">
                          <span className="cat-name" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.supplier_name} · {s.order_count} đơn
                          </span>
                          <span className="cat-amount">{formatVND(s.invoice_value)}</span>
                        </div>
                        <div className="cat-bar-track">
                          <div className="cat-bar-fill" style={{ width: `${supplierMax > 0 ? Math.max(2, (s.invoice_value / supplierMax) * 100) : 2}%`, background: "var(--accent)" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {analytics.goods_types.length > 0 && (
                <div>
                  <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Theo loại hàng</h4>
                  <div className="cat-bars">
                    {analytics.goods_types.map((g) => (
                      <div className="cat-row" key={g.goods_type} style={{ cursor: "default" }}>
                        <div className="cat-row-head">
                          <span className="cat-name">{GOODS_TYPE_LABEL[g.goods_type] || g.goods_type} · {g.order_count} đơn</span>
                          <span className="cat-amount">{formatVND(g.invoice_value)}</span>
                        </div>
                        <div className="cat-bar-track">
                          <div className="cat-bar-fill" style={{ width: `${goodsMax > 0 ? Math.max(2, (g.invoice_value / goodsMax) * 100) : 2}%`, background: "var(--accent-green)" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </GlassPanel>

      <GlassPanel title={panelTitle} badge={`${filtered.length} đơn đã tải`}>
        <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 12 }}>
          {panelDescription}
        </p>
        {emphasizeInvoice && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "8px 12px", background: "rgba(52,211,153,0.08)", borderRadius: 8, fontSize: 13 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--accent-green)" }}>receipt_long</span>
            <span>Đính kèm hoặc quét hóa đơn VAT khi tạo đơn — bấm <strong>Xem hóa đơn</strong> trên từng đơn đã có file.</span>
          </div>
        )}
        {/* Toolbar — compact search + date range + status + clear + create. */}
        <style>{`
          .compact-import-filter {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
            margin-bottom: 12px;
          }
          .compact-import-filter .search {
            flex: 1 1 160px;
            min-width: 160px;
          }
          .compact-import-filter .search input {
            font-size: 13px;
            padding: 6px 8px 6px 32px;
          }
          .compact-import-filter input[type="date"] {
            width: 130px;
            font-size: 13px;
            padding: 6px 8px;
          }
          .compact-import-filter .ui-select__trigger {
            min-height: 32px;
            font-size: 13px;
            padding: 6px 10px;
          }
          .compact-import-filter .btn-icon {
            width: 36px;
            height: 32px;
          }
          @media (max-width: 768px) {
            .compact-import-filter .search,
            .compact-import-filter input[type="date"],
            .compact-import-filter .ui-select,
            .compact-import-filter .btn-icon,
            .compact-import-filter .btn {
              flex: 1 1 calc(50% - 3px);
              min-width: 120px;
            }
            .compact-import-filter .search input,
            .compact-import-filter input[type="date"],
            .compact-import-filter .ui-select__trigger {
              width: 100%;
            }
          }
        `}</style>
        <div className="filter-bar compact-import-filter">
          <div className="search">
            <span className="material-symbols-outlined">search</span>
            <input
              placeholder="Tìm NCC / số HĐ..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="Từ ngày mua" aria-label="Từ ngày" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="Đến ngày mua" aria-label="Đến ngày" />
          <div style={{ width: "auto", minWidth: 110 }}>
            <Select
              value={statusFilter}
              ariaLabel="Trạng thái"
              options={[
                { value: "", label: "Tất cả trạng thái" },
                { value: "pending", label: "Chờ nhập" },
                { value: "imported", label: "Đã nhập" },
                { value: "cancelled", label: "Đã hủy" },
              ]}
              onChange={(s) => setStatusFilter(s)}
            />
          </div>
          {(dateFrom || dateTo || searchInput || statusFilter) && (
            <button
              className="btn-icon"
              type="button"
              onClick={() => { setDateFrom(""); setDateTo(""); setStatusFilter(""); setSearchInput(""); }}
              title="Xóa filter"
              aria-label="Xóa filter"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
            </button>
          )}
          {!readOnly && (
            <button className="btn btn-icon" type="button" onClick={openCreateModal} title="Tạo đơn nhập hàng mới" aria-label="Tạo đơn nhập hàng mới">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
            </button>
          )}
        </div>

        {loading ? (
          <SkeletonTable rows={5} cols={5} />
        ) : error ? (
          <ErrorState error={error} onRetry={loadOrders} />
        ) : sortedForDisplay.length === 0 ? (
          <EmptyState icon="local_shipping" title="Chưa có đơn nhập hàng" description="Tạo đơn nhập để theo dõi hàng nhập kho." />
        ) : isDesktop ? (
          <DataTable
            columns={tableColumns}
            rows={sortedForDisplay}
            rowKey={(o) => String(o.id)}
            pageSize={50}
            defaultSort={{ key: "created_at", dir: "desc" }}
            emptyMessage="Chưa có đơn nhập hàng"
          />
        ) : (
          <div className="list">
            {sortedForDisplay.map((o) => (
              <div
                className="list-item"
                key={o.id}
                style={{ cursor: "pointer", flexDirection: "column", alignItems: "stretch", gap: 6 }}
                onClick={() => setDetailOrder(o)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="name">
                      #{o.id} — {o.supplier_name || "Không tên"}
                    </div>
                    <div className="meta">
                      Mua: {formatDate(o.order_date)}
                      {o.created_at && ` · Tạo: ${formatDate(o.created_at)}`}
                      {o.received_at && ` · Nhập: ${formatDate(o.received_at)}`}
                      {o.goods_type && o.goods_type !== "tieungach" && ` · ${GOODS_TYPE_LABELS[o.goods_type as GoodsType] || o.goods_type}`}
                      {o.invoice_no && ` · HĐ ${o.invoice_no}`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div className="amount">{formatVND(o.total_amount)}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "space-between" }}>
                  <Badge variant={statusVariant(o.status)}>
                    {statusText(o.status)}
                  </Badge>
                  <div style={{ display: "flex", gap: 4 }}>
                    {o.invoice_file_id && (
                      <button
                        className="btn-icon"
                        type="button"
                        onClick={(e) => { e.stopPropagation(); viewInvoice(o); }}
                        title="Xem hóa đơn đính kèm"
                        aria-label="Xem HĐ"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>receipt</span>
                      </button>
                    )}
                    {o.status === "pending" && (
                      <button
                        className="btn-icon"
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleMarkImported(o); }}
                        disabled={marking || deleting}
                        title="Xác nhận hàng đã về kho"
                        aria-label="Đã nhập kho"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>

      {/* Modal tạo / sửa đơn nhập */}
      <Modal
        open={showModal}
        onClose={attemptClose}
        title={editingId ? `Sửa đơn nhập #${editingId}` : "Tạo đơn nhập hàng"}
        width={800}
        pinFooter
        footer={
          <div className="import-order-bar">
            {selectedIdxs.size > 0 && (
              <div className="bulk-toolbar" role="region" aria-label="Thao tác hàng loạt">
                <span className="bulk-toolbar__count">{selectedIdxs.size} dòng</span>
                <button type="button" className="bulk-toolbar__clear" onClick={clearBulkSelect}>
                  bỏ chọn
                </button>
                <input
                  id="bulk-name-input"
                  type="text"
                  placeholder="Tên chung"
                  value={bulkName}
                  onChange={(e) => setBulkName(e.target.value)}
                  className="bulk-toolbar__input"
                  aria-label="Tên chung cho các dòng đã chọn"
                />
                <button
                  type="button"
                  className="bulk-toolbar__apply"
                  onClick={() => {
                    const name = bulkName.trim();
                    if (!name) { toast.error("Nhập tên trước khi áp dụng"); return; }
                    const n = selectedIdxs.size;
                    setForm((prev) => {
                      const next = [...prev.items];
                      selectedIdxs.forEach((i) => {
                        if (next[i]) next[i] = { ...next[i], item_name: name };
                      });
                      return { ...prev, items: next };
                    });
                    toast.success(`Đã đổi tên ${n} dòng`);
                  }}
                >
                  Đổi tên
                </button>
                <div className="bulk-toolbar__select">
                  <Select<string>
                    id="bulk-cat-select"
                    value={bulkCatId}
                    options={[{ value: "", label: "Danh mục" }, ...categories.map((c) => ({ value: c.id, label: c.name }))]}
                    onChange={setBulkCatId}
                    ariaLabel="Chọn danh mục gán hàng loạt"
                    searchable
                    searchPlaceholder="Tìm danh mục…"
                  />
                </div>
                <button
                  type="button"
                  className="bulk-toolbar__apply"
                  title="Chỉ áp dụng dòng đã bật Hàng mới"
                  onClick={() => {
                    if (!bulkCatId) { toast.error("Chọn danh mục trước khi áp dụng"); return; }
                    const m = form.items.reduce(
                      (acc, it, i) => acc + (selectedIdxs.has(i) && it.is_new_product ? 1 : 0),
                      0,
                    );
                    if (m === 0) {
                      toast.error("Không có dòng «Hàng mới» trong lựa chọn");
                      return;
                    }
                    setForm((prev) => {
                      const next = [...prev.items];
                      selectedIdxs.forEach((i) => {
                        if (next[i] && next[i].is_new_product) {
                          next[i] = { ...next[i], category_id: bulkCatId };
                        }
                      });
                      return { ...prev, items: next };
                    });
                    toast.success(`Đã gán danh mục ${m} dòng hàng mới`);
                  }}
                >
                  Gán
                </button>
              </div>
            )}
            <div className="import-order-bar__row">
            <div className="import-order-bar__tools">
              {(() => {
                const namedIdxs = form.items
                  .map((it, i) => (it.item_name.trim() ? i : -1))
                  .filter((i) => i >= 0);
                const allNamedSelected = namedIdxs.length > 0 && namedIdxs.every((i) => selectedIdxs.has(i));
                return (
                  <button
                    type="button"
                    className="btn btn-ghost btn-compact"
                    disabled={namedIdxs.length === 0}
                    title={allNamedSelected ? "Bỏ chọn tất cả dòng có tên" : "Chọn tất cả dòng có tên"}
                    onClick={() => {
                      if (allNamedSelected) setSelectedIdxs(new Set());
                      else setSelectedIdxs(new Set(namedIdxs));
                    }}
                  >
                    {allNamedSelected ? "Bỏ chọn" : "Chọn hết"}
                  </button>
                );
              })()}
              <button type="button" className="btn btn-ghost btn-compact" style={{ color: "#a78bfa" }} disabled={aiPricingIdx === -2} onClick={suggestPriceAll} title="AI gợi giá bán cho tất cả dòng">
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>{aiPricingIdx === -2 ? "hourglass_top" : "auto_awesome"}</span>
                {aiPricingIdx === -2 ? "Đang gợi…" : "Gợi giá"}
              </button>
              <button type="button" className="btn btn-ghost btn-compact" disabled={mergedOrder} title={mergedOrder ? "Không thêm/xoá dòng trên đơn đã gộp" : "Thêm 1 dòng trống"} onClick={() => { if (!mergedOrder) setForm({ ...form, items: [...form.items, emptyItem()] }); }}>
                + Dòng
              </button>
            </div>
            <button className="btn btn-compact import-order-bar__save" onClick={handleSubmit} disabled={submitting} title={editingId ? "Lưu thay đổi cho đơn nhập này" : "Tạo đơn nhập mới với các SP đã nhập"}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>save</span>
              {submitting ? "Đang lưu…" : (editingId ? "Lưu đơn" : "Tạo đơn")}
            </button>
            </div>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <ImportOrderAiChat
            key={aiChatKey}
            ref={aiChatRef}
            form={form}
            orderId={editingId}
            autoOpenAttach={scanAttachOnce}
            onFormPatch={(patch) => {
              setForm((prev) => {
                const next = normalizeAiForm(patch, prev);
                if (
                  next.supplier_phone ||
                  next.supplier_address ||
                  next.supplier_tax_id ||
                  next.supplier_bank_account
                ) {
                  setShowMoreDetails(true);
                }
                return next;
              });
              // Chat-set supplier linkage → debt tracking (server resolves supplier_id; normalizeAiForm drops it)
              if (patch?.supplier_id !== undefined) {
                setSelectedSupplierId(patch.supplier_id != null && String(patch.supplier_id).trim() ? String(patch.supplier_id) : null);
              }
              setTimeout(() => {
                productsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 150);
            }}
            onLocalFile={(f) => setInvoiceFile(f)}
            onOrderSaved={(id) => {
              setEditingId(id);
              void loadOrders();
              void loadAnalytics();
            }}
            onNeedReload={() => {
              void loadOrders();
              void loadAnalytics();
            }}
          />

          {editingId && <ImportAttachments orderId={editingId} />}

          {!editingId && (emphasizeInvoice || form.goods_type === "vat" || form.goods_type === "bangke" || form.goods_type === "vn") && (
            <div>
              <label style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 600, display: "block", marginBottom: 6 }}>
                Đính kèm ảnh/PDF hóa đơn (lưu kèm đơn)
              </label>
              <FilePicker
                variant="dropzone"
                accept="image/*,application/pdf"
                label="Chọn hoặc kéo thả ảnh/PDF"
                hint="Lưu kèm đơn để tra cứu sau — hoặc gửi trong chat AI"
                icon="attach_file"
                file={invoiceFile}
                onSelect={(f) => setInvoiceFile(f)}
              />
            </div>
          )}

          {invoiceFile && !(emphasizeInvoice || form.goods_type === "vat" || form.goods_type === "bangke" || form.goods_type === "vn") && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-secondary)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>attach_file</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{invoiceFile.name}</span>
              <button
                type="button"
                onClick={() => setInvoiceFile(null)}
                title="Bỏ file"
                style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--accent-red)", padding: 0, display: "flex" }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
              </button>
            </div>
          )}

          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ngày đặt *</label>
          <input type="date" value={form.order_date} disabled={mergedOrder} onChange={(e) => { if (!mergedOrder) setForm({ ...form, order_date: e.target.value }); }} />
          {mergedOrder && <div className="form-hint">Không đổi ngày nhập</div>}

          {/* Loại hàng đặt đầu form; khi thay đổi tự cập nhật flags items. Edit mode vẫn giữ gt gốc. */}
          {!lockGoodsType && (
            <>
              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Loại hàng *</label>
              <Select
                value={form.goods_type}
                ariaLabel="Loại hàng"
                disabled={mergedOrder}
                options={goodsTypeOptions.map((gt) => ({
                  value: gt,
                  label: GOODS_TYPE_LABELS[gt],
                }))}
                onChange={(gt) => {
                  const flags = itemFlagsForGoodsType(gt);
                  setForm({
                    ...form,
                    goods_type: gt,
                    items: form.items.map((it) => (it.merge_target ? it : { ...it, ...flags })),
                  });
                }}
              />
              {mergedOrder && <div className="form-hint">Không đổi loại đơn</div>}
            </>
          )}

          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Nhà cung cấp *</label>
          <div style={{ position: "relative" }}>
            <input
              placeholder="Tên NCC"
              value={form.supplier_name}
              autoComplete="off"
              onChange={(e) => {
                setForm({ ...form, supplier_name: e.target.value });
                setSelectedSupplierId(null);
                setSupplierSuggestOpen(true);
              }}
              onFocus={() => setSupplierSuggestOpen(true)}
              onBlur={(e) => { loadSupplierAi(e.target.value); setTimeout(() => setSupplierSuggestOpen(false), 150); }}
            />
            {supplierSuggestOpen && supplierSuggestions.length > 0 && (
              <div
                style={{
                  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
                  marginTop: 2, maxHeight: 220, overflowY: "auto",
                  background: "var(--surface, #1b1d24)", border: "1px solid var(--border, #2a2d36)",
                  borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                }}
              >
                {supplierSuggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="btn btn-ghost"
                    style={{ width: "100%", justifyContent: "flex-start", textAlign: "left", borderRadius: 0, fontSize: 13, padding: "8px 12px" }}
                    onMouseDown={(e) => { e.preventDefault(); pickSupplier(s); }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}>
                      <span style={{ fontWeight: 600 }}>{s.name}</span>
                      {(s.tax_id || s.phone) && (
                        <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                          {[s.tax_id ? `MST/CCCD: ${s.tax_id}` : "", s.phone || ""].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {form.supplier_name.trim() && !supplierExactMatch && selectedSupplierId === null && (
            <div style={{ fontSize: 11, color: "var(--accent-ai, #a78bfa)", marginTop: -4 }}>
              NCC mới — thông tin sẽ được lưu lại
            </div>
          )}

          {activeAi && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "rgba(167,139,250,0.08)", borderRadius: 6, fontSize: 12 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--accent-ai, #a78bfa)" }}>auto_awesome</span>
              <span>AI: <strong>{activeAi.provider}</strong> / {activeAi.model}</span>
              <Badge variant="info">{activeAi.source === "supplier" ? "theo NCC" : "global"}</Badge>
            </div>
          )}

          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Tổng tiền *</label>
          <NumField value={form.total_amount || null} emptyValue={0} onCommit={(v) => setForm({ ...form, total_amount: v ?? 0 })} />
          {form.goods_type === "vat" && (
            <div>
              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số hóa đơn VAT</label>
              <input placeholder="Số HĐ (nếu có)" value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} />
            </div>
          )}

          <button
            type="button"
            className="btn btn-ghost"
            style={{ width: "100%", justifyContent: "space-between", fontSize: 13 }}
            onClick={() => setShowMoreDetails((v) => !v)}
          >
            <span>Chi tiết thêm</span>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              {showMoreDetails ? "expand_less" : "expand_more"}
            </span>
          </button>
          {showMoreDetails && (
            <>
              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số điện thoại</label>
              <input placeholder="Số ĐT" value={form.supplier_phone} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })} />

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Địa chỉ NCC</label>
              <input placeholder="Địa chỉ" value={form.supplier_address} onChange={(e) => setForm({ ...form, supplier_address: e.target.value })} />

              {(form.goods_type === "bangke" || form.goods_type === "vn") && (
                <>
                  <label style={{ fontSize: 12, color: "var(--accent, #7c5cff)" }}>MST / CCCD người bán <span style={{ color: "var(--text-tertiary)" }}>(cần cho Bảng kê 01/TNDN)</span></label>
                  <input placeholder="Mã số thuế hoặc CCCD" value={form.supplier_tax_id} onChange={(e) => setForm({ ...form, supplier_tax_id: e.target.value })} />

                  <label style={{ fontSize: 12, color: "var(--accent, #7c5cff)" }}>Số tài khoản ngân hàng</label>
                  <input placeholder="STK + tên ngân hàng" value={form.supplier_bank_account} onChange={(e) => setForm({ ...form, supplier_bank_account: e.target.value })} />
                </>
              )}

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Đã trả</label>
              <NumField value={form.paid_amount || null} emptyValue={0} onCommit={(v) => setForm({ ...form, paid_amount: v ?? 0 })} />

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Hạn trả nợ</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ghi chú</label>
              <textarea placeholder="Ghi chú đơn nhập…" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} style={{ resize: "vertical" }} />
            </>
          )}

          {/* Sản phẩm nhập */}
          <div ref={productsSectionRef} style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 12, marginTop: 4 }}>
            {/* Phí ship + phụ phí chung → chia theo số lượng vào giá vốn từng SP */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10, padding: "8px 10px", background: "rgba(124,158,255,0.06)", borderRadius: 8 }}>
              <span style={{ fontSize: 12.5, opacity: 0.85 }}>🚚 Phí ship + phụ phí chung:</span>
              <NumField placeholder="0" value={form.extra_fee || null} emptyValue={0} onCommit={(v) => setForm({ ...form, extra_fee: v ?? 0 })} style={{ width: 120, fontSize: 13, padding: "6px 10px" }} />
              <span style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>
                {(() => {
                  const tq = form.items.reduce((s, i) => s + retailPieceQty(i), 0);
                  return tq > 0 && form.extra_fee > 0 ? `→ chia ${formatVND(Math.round(form.extra_fee / tq))}/cái vào giá vốn` : "chia đều theo số lượng";
                })()}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}>
                Sản phẩm nhập
                {form.goods_type === "vat" && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 6, color: "#60a5fa", background: "rgba(96,165,250,0.14)" }}>Cả đơn: VAT</span>}
                {(form.goods_type === "bangke" || form.goods_type === "vn") && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 6, color: "#fbbf24", background: "rgba(251,191,36,0.14)" }}>Cả đơn: Bảng kê</span>}
              </span>
            </div>
            {saveConflict && saveConflict.length > 0 && (
              <div role="alert" style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(248,113,113,0.45)", background: "rgba(248,113,113,0.08)", fontSize: 12, color: "var(--accent-red, #f87171)" }}>
                <div style={{ fontWeight: 700 }}>{saveConflict[0]}</div>
                {saveConflict.length > 1 && (
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                    {saveConflict.slice(1).map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                )}
              </div>
            )}
            {form.items.map((item, idx) => (
              <div key={idx} style={{ marginBottom: 6, padding: 6, background: "rgba(255,255,255,0.02)", borderRadius: 6 }}>
                {/* Row A: checkbox + # + name + SL + Mỗi + Giá vốn + delete */}
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <input type="checkbox" checked={selectedIdxs.has(idx)} aria-label={`Chọn dòng ${idx + 1}`} onChange={() => {
                    const next = new Set(selectedIdxs);
                    if (next.has(idx)) next.delete(idx); else next.add(idx);
                    setSelectedIdxs(next);
                  }} style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--accent, #4f9eff)" }} />
                  <span style={{ flex: "0 0 auto", fontSize: 12, color: "var(--text-tertiary)", minWidth: 20, textAlign: "right" }} title="Số thứ tự dòng">{idx + 1}</span>
                  <div style={{ position: "relative", flex: "1 1 160px", minWidth: 140 }}>
                    <input
                      placeholder="Tên SP (gõ để tìm)"
                      value={item.item_name}
                      onChange={(e) => { updateFormItem(idx, "item_name", e.target.value); handleSearch(e.target.value, idx); }}
                      onBlur={() => setTimeout(() => setSearchResults([]), 200)}
                      style={{ fontSize: 12, width: "100%" }}
                    />
                    {activeItemIdx === idx && searchResults.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: 6, zIndex: 100, maxHeight: 160, overflowY: "auto" }}>
                        {searchResults.map((r) => (
                          <div
                            key={r.id}
                            style={{ padding: "6px 10px", cursor: "pointer", fontSize: 12 }}
                            onMouseDown={() => { updateFormItem(idx, "item_name", r.item_name); if (r.cost && r.cost > 0) updateFormItem(idx, "unit_cost", r.cost); setSearchResults([]); }}
                          >
                            {r.item_name}{r.sku ? ` (${r.sku})` : ""}{r.cost ? ` — ${formatVND(r.cost)}` : ""}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <NumField placeholder="SL lốc" value={item.quantity || null} emptyValue={1} onCommit={(v) => {
                    const nextQty = v ?? 1;
                    setForm((prev) => {
                      const items = [...prev.items];
                      const cur = items[idx];
                      if (!cur) return prev;
                      const next = { ...cur, quantity: nextQty };
                      if (cur.total_qty == null) {
                        const pack = Number(cur.per_pack) || 0;
                        const oldQty = Number(cur.quantity) || 0;
                        const shown = pack > 0 ? oldQty * pack : oldQty;
                        if (shown > 0) next.total_qty = shown;
                      }
                      items[idx] = next;
                      return { ...prev, items };
                    });
                  }} style={{ fontSize: 12, width: 64, minWidth: 64, flex: "0 0 auto", paddingRight: 8 }} title="Số lốc. Sửa số này không kéo ô Tổng (số cái)." />
                  <NumField placeholder="Mỗi" value={item.per_pack != null && Number(item.per_pack) > 1 ? item.per_pack : null} emptyValue={null} onCommit={(v) => {
                    const pack = v != null && v > 1 ? v : null;
                    const qty = Number(item.quantity) || 0;
                    setForm((prev) => {
                      const items = [...prev.items];
                      items[idx] = { ...items[idx], per_pack: pack, total_qty: pack && qty ? qty * pack : items[idx].total_qty };
                      return { ...prev, items };
                    });
                  }} style={{ fontSize: 12, width: 72, minWidth: 72, flex: "0 0 auto", paddingRight: 8 }} title="Mỗi lốc có bao nhiêu cái (vd 10). Để trống nếu không pack. 1 = không pack." />
                  <NumField placeholder="Giá vốn" value={item.unit_cost || null} emptyValue={0} onCommit={(v) => updateFormItem(idx, "unit_cost", v ?? 0)} style={{ fontSize: 12, width: 96, minWidth: 96, flex: "0 0 auto", paddingRight: 8 }} title="Giá vốn 1 lốc (từ hóa đơn)" />
                  <button title={mergedOrder ? "Không thêm/xoá dòng trên đơn đã gộp" : "Xóa dòng"} disabled={mergedOrder} style={{ color: "var(--accent-red)", fontSize: 16, flex: "0 0 auto", padding: "0 4px", opacity: mergedOrder ? 0.35 : 1 }} onClick={() => { if (!mergedOrder) setForm({ ...form, items: form.items.filter((_, i) => i !== idx) }); }}>×</button>
                </div>
                {/* Row B: meta — Tổng + vốn lẻ + Bán + % + Hàng mới | NL | unit/cat — one dense wrap row */}
                {(() => {
                  const pack = Number(item.per_pack) || 0;
                  const qty = Number(item.quantity) || 0;
                  const totalDefault = pack > 0 ? qty * pack : qty;
                  const costPerUnit = costPerRetailPiece(item, 0);
                  const tq = form.items.reduce((s, i) => s + retailPieceQty(i), 0);
                  const feePerUnit = tq > 0 ? (Number(form.extra_fee) || 0) / tq : 0;
                  const costPerRetail = costPerRetailPiece(item, feePerUnit);
                  const pct = item.sell_price && costPerRetail > 0 ? Math.round((item.sell_price / costPerRetail - 1) * 100) : null;
                  return (
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                      <span style={{ fontSize: 11.5, color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>Tổng</span>
                      <NumField placeholder={String(totalDefault || "")} value={item.total_qty ?? (totalDefault || null)} emptyValue={null} onCommit={(v) => updateFormItem(idx, "total_qty", v || null)} style={{ fontSize: 12, width: 64, minWidth: 64, paddingRight: 6 }} title="Tổng số cái thực nhập kho (mặc định = SL lốc × mỗi lốc, chỉnh xuống nếu xài mất 1-2 cái)" />
                      <span style={{ fontSize: 11.5, color: "var(--accent-green)", whiteSpace: "nowrap" }}>
                        vốn {formatVND(Math.round(costPerUnit))}/cái
                      </span>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 11.5, color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>Bán</span>
                        <NumField
                          placeholder={costPerRetail > 0 ? String(Math.round((costPerRetail * 2.1) / 1000) * 1000) : "giá bán"}
                          value={item.sell_price || null}
                          emptyValue={null}
                          onCommit={(v) => updateFormItem(idx, "sell_price", v || null)}
                          style={{ fontSize: 12, width: 92, minWidth: 88, flex: "0 0 auto", paddingRight: 4 }}
                        />
                      </label>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 11.5, color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>%</span>
                        <NumField
                          placeholder="110"
                          value={pct}
                          emptyValue={null}
                          onCommit={(p) => {
                            if (p != null && costPerRetail > 0) updateFormItem(idx, "sell_price", Math.round((costPerRetail * (1 + p / 100)) / 1000) * 1000);
                          }}
                          style={{ fontSize: 12, width: 64, minWidth: 64, flex: "0 0 64px", paddingRight: 4 }}
                          title="Gõ % lời → tự tính giá bán (giá vốn lẻ × (1 + %/100), làm tròn nghìn)"
                        />
                      </label>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        aria-pressed={!!item.is_new_product}
                        disabled={mergedOrder}
                        style={{ fontSize: 11, padding: "2px 8px", minHeight: 26, border: item.is_new_product ? "1px solid #34d399" : "1px solid var(--border-subtle)", color: item.is_new_product ? "#34d399" : "var(--text-tertiary)", display: "inline-flex", alignItems: "center", gap: 3, opacity: mergedOrder ? 0.45 : 1 }}
                        onClick={() => {
                          if (mergedOrder) return;
                          const turnOn = !item.is_new_product;
                          updateFormItem(idx, "is_new_product", turnOn);
                          if (turnOn) { updateFormItem(idx, "mapped_item_id", null); updateFormItem(idx, "mapped_item_name", null); }
                        }}
                        title={mergedOrder ? "Không tạo sản phẩm mới từ đơn đã gộp" : "Hàng mới chưa có trên POS — khi nhập kho sẽ tạo SP mới (tên = tên SP ở trên, số lượng = SL ở trên)"}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{item.is_new_product ? "check_circle" : "add_box"}</span>
                        Hàng mới
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        aria-pressed={!!item.is_material}
                        disabled={mergedOrder}
                        style={{ fontSize: 11, padding: "2px 8px", minHeight: 26, border: item.is_material ? "1px solid #34d399" : "1px solid var(--border-subtle)", color: item.is_material ? "#34d399" : "var(--text-tertiary)", display: "inline-flex", alignItems: "center", gap: 3, opacity: mergedOrder ? 0.45 : 1 }}
                        onClick={() => {
                          if (mergedOrder) return;
                          const turnOn = !item.is_material;
                          updateFormItem(idx, "is_material", turnOn);
                          if (turnOn && !item.unit) updateFormItem(idx, "unit", "kg");
                        }}
                        title={mergedOrder ? "Dòng nguyên liệu chưa hỗ trợ sửa" : "Đây là nguyên liệu (D1, không lên POS)"}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{item.is_material ? "check_circle" : "science"}</span>
                        NL
                      </button>
                      {item.is_material && (
                        <div style={{ flex: "0 0 auto", width: 92 }}>
                          <Select<string>
                            value={(item.unit as "kg" | "g" | "ml" | "l" | "pcs") || "kg"}
                            options={[
                              { value: "kg", label: "kg" },
                              { value: "g", label: "g" },
                              { value: "ml", label: "ml" },
                              { value: "l", label: "l" },
                              { value: "pcs", label: "pcs" },
                            ]}
                            onChange={(v) => updateFormItem(idx, "unit", v)}
                            ariaLabel="Đơn vị nguyên liệu"
                          />
                        </div>
                      )}
                      {item.is_new_product && (
                        <div style={{ flex: "0 0 auto", width: 160 }}>
                          <Select<string>
                            value={item.category_id ?? ""}
                            options={[{ value: "", label: "— Danh mục —" }, ...categories.map((c) => ({ value: c.id, label: c.name }))]}
                            onChange={(v) => updateFormItem(idx, "category_id", v || null)}
                            ariaLabel="Danh mục hàng mới"
                            searchable
                            searchPlaceholder="Tìm danh mục…"
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* Row C: map — only when not Hàng mới */}
                {!item.is_new_product && (
                <div style={{ position: "relative", marginTop: 4 }}>
                  {item.mapped_item_id ? (
                    // ĐÃ map → chip khóa, KHÔNG cho gõ (gõ sẽ clear id → nhập kho đẻ SP mới + mất map cũ).
                    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(52,211,153,0.4)", background: "rgba(52,211,153,0.06)" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--accent-green)" }}>check_circle</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--accent-green)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>đã map: {item.mapped_item_name}</span>
                      {!mergedOrder && (
                      <button
                        type="button"
                        onClick={() => { updateFormItem(idx, "mapped_item_id", null); updateFormItem(idx, "mapped_item_name", null); }}
                        style={{ fontSize: 11, padding: "2px 8px", color: "var(--text-tertiary)", border: "1px solid var(--border-subtle)", borderRadius: 6, background: "transparent", cursor: "pointer", flex: "0 0 auto" }}
                        title="Bỏ map để chọn SP khác"
                      >Bỏ map</button>
                      )}
                    </div>
                  ) : (
                  <div style={{ position: "relative" }}>
                    <span className="material-symbols-outlined" style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "var(--text-tertiary)", pointerEvents: "none" }}>link</span>
                    <input
                      placeholder="Map SP POS…"
                      value={item.mapped_item_name || ""}
                      disabled={mergedOrder}
                      title={mergedOrder ? "Không đổi được sản phẩm map" : "Map dòng này với sản phẩm POS đang bán — để truy ngược nguồn khi khai thuế"}
                      onChange={(e) => {
                        if (mergedOrder) return;
                        const v = e.target.value;
                        updateFormItem(idx, "mapped_item_name", v);
                        handleMapSearch(v, idx);
                      }}
                      onBlur={() => setTimeout(() => setMapResults({}), 200)}
                      style={{ fontSize: 12, width: "100%", padding: "6px 10px 6px 32px", minHeight: 34, borderRadius: 6 }}
                    />
                    </div>
                  )}
                  {!item.mapped_item_id && mapResults[idx] && mapResults[idx].length > 0 && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: 8, zIndex: 100, maxHeight: 200, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
                      {mapResults[idx].map((r) => (
                        <div
                          key={r.id}
                          style={{ padding: "10px 12px", cursor: "pointer", fontSize: 13.5, minHeight: 44, display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid var(--border-subtle)" }}
                          onMouseDown={() => {
                            updateFormItem(idx, "mapped_item_id", r.id);
                            updateFormItem(idx, "mapped_item_name", r.item_name);
                            // KHÔNG đổi tên dòng đơn nhập theo SP map — giữ tên gốc trên bill.
                            // Kéo giá bán hiện tại của SP cũ vào ô (để trống = giữ, sửa = cập nhật khi nhập kho).
                            if (r.default_price && r.default_price > 0) updateFormItem(idx, "sell_price", r.default_price);
                            setMapResults({});
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(124,158,255,0.10)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--text-tertiary)" }}>inventory_2</span>
                          <span>{r.item_name}{r.sku ? <span style={{ opacity: 0.6 }}> · {r.sku}</span> : ""}{r.default_price ? <span style={{ opacity: 0.6 }}> · {formatVND(r.default_price)}</span> : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}
                {item.merge_target && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(52,211,153,0.4)", background: "rgba(52,211,153,0.06)" }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14, color: "var(--accent-green)" }} aria-hidden="true">merge</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--accent-green)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{mergeTargetLabel(item.merge_target)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Detail modal */}
      <Modal
        open={!!detailOrder}
        onClose={() => setDetailOrder(null)}
        title={detailOrder ? `Đơn nhập #${detailOrder.id}` : ""}
      >
        {detailOrder && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Nút hành động — đưa lên đầu để khỏi cuộn xuống cuối. Grid 2 cột: nút chính full-width, phụ chia đôi. */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, width: "100%", paddingBottom: 8, borderBottom: "1px solid var(--border-subtle)" }}>
              {/* Sửa đơn: mọi trạng thái. Đơn đã nhập kho — sửa tên/giá dòng đã map sẽ đẩy lên POS. */}
              <button
                className="btn btn-ghost btn-compact"
                style={{ fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, color: "#7c9eff", border: "1px solid rgba(124,158,255,0.4)", whiteSpace: "nowrap" }}
                onClick={() => detailOrder && openEditOrder(detailOrder)}
                disabled={marking || deleting}
                title="Mở form đầy đủ: sửa SP, lẻ ra, phí ship, giá bán, map/hàng mới. Đơn đã nhập kho → sửa SP đã map sẽ đẩy lên POS."
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                {detailOrder.status === "imported" ? "Sửa đơn (đẩy POS)" : "Sửa đơn"}
              </button>
              {detailOrder.status === "pending" && (
                <>
                  <button
                    className="btn btn-compact"
                    style={{ fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, whiteSpace: "nowrap" }}
                    onClick={() => detailOrder && handleMarkImported(detailOrder)}
                    disabled={marking || deleting}
                    title={(detailOrder.paid_amount || 0) > 0
                      ? `Xác nhận hàng đã về kho: cộng tồn cho SP đã map, tạo SP mới, đẩy lên Loyverse. Đơn đã trả ${formatVND(detailOrder.paid_amount)} → giữ nguyên công nợ còn lại.`
                      : "Xác nhận hàng đã về kho: cộng tồn cho SP đã map, tạo SP mới, đẩy lên Loyverse. Mặc định đánh dấu ĐÃ THANH TOÁN ĐỦ (ghi sổ, không tạo giao dịch tiền mặt)."}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{marking ? "hourglass_top" : "inventory"}</span>
                    {marking ? "Đang xử lý…" : (detailOrder.paid_amount || 0) > 0 ? "Nhập kho (giữ thanh toán đã ghi)" : "Nhập kho và đánh dấu đã thanh toán"}
                  </button>
                </>
              )}
              {detailOrder && (detailOrder.goods_type === "bangke" || detailOrder.goods_type === "vn") && (
                <button
                  className="btn btn-ghost btn-compact"
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, color: "#60a5fa" }}
                  onClick={() => detailOrder && handleAttachVat(detailOrder)}
                  disabled={marking || deleting}
                  title="NCC đã xuất hóa đơn VAT cho lô hàng này → chuyển đơn bảng kê sang VAT"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>receipt_long</span>
                  Đính VAT
                </button>
              )}
              {/* Đổi loại đơn (tiểu ngạch ↔ bảng kê ↔ VAT ↔ VN) mà không cần xóa tạo lại */}
              {!showGoodsPicker ? (
                <button
                  className="btn btn-ghost btn-compact"
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, color: "var(--text-secondary)" }}
                  onClick={() => detailOrder && handleChangeGoodsType(detailOrder)}
                  disabled={marking || deleting}
                  title="Đổi loại đơn nhập (tiểu ngạch / bảng kê / VAT / hàng VN) — không cần xóa tạo lại"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>swap_horiz</span>
                  Đổi loại
                </button>
              ) : (
                <div style={{ gridColumn: "span 2", border: "1px solid var(--border-subtle)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 2 }}>Chọn loại đơn mới:</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    {([["tieungach", "📦 Tiểu ngạch"], ["bangke", "📋 Bảng kê"], ["vat", "🧾 Có VAT"]] as const).map(([key, label]) => (
                      <button
                        key={key}
                        className="btn btn-ghost"
                        style={{ minHeight: 38, fontSize: 13, justifyContent: "center", gap: 6, opacity: detailOrder?.goods_type === key ? 0.4 : 1 }}
                        disabled={detailOrder?.goods_type === key}
                        onClick={() => applyGoodsType(key)}
                      >
                        {label}{detailOrder?.goods_type === key ? " ✓" : ""}
                      </button>
                    ))}
                  </div>
                  <button className="btn btn-ghost" style={{ fontSize: 12, color: "var(--text-tertiary)" }} onClick={() => setShowGoodsPicker(false)}>Huỷ</button>
                </div>
              )}
              {detailOrder.status === "imported" && (() => {
                const remainN = unmappedNewItems(detailOrder).length;
                if (remainN === 0) return null;
                return (
                  <button
                    className="btn btn-compact"
                    style={{
                      gridColumn: "span 2",
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                      background: "rgba(251,191,36,0.16)",
                      color: "#fbbf24",
                      border: "1px solid rgba(251,191,36,0.55)",
                    }}
                    onClick={() => detailOrder && handleCreateRemainingProducts(detailOrder)}
                    disabled={marking || deleting}
                    title="Đơn đã nhập nhưng còn dòng hàng mới chưa tạo SP trên POS — bấm để tạo phần còn lại (idempotent)"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                      {marking ? "hourglass_top" : "add_box"}
                    </span>
                    {marking ? "Đang tạo…" : `Tạo SP còn lại (${remainN})`}
                  </button>
                );
              })()}
              {detailOrder.status === "imported" && (
                <button
                  className="btn btn-ghost btn-compact"
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, color: "#34d399" }}
                  onClick={() => detailOrder && handlePrintLabels(detailOrder)}
                  title="In tem mã vạch cho các SP trong đơn, theo đúng số lượng nhập"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>barcode</span>
                  In tem
                </button>
              )}
              <button
                className="btn btn-ghost btn-compact"
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, color: "var(--accent-red)" }}
                onClick={() => detailOrder && handleDeleteOrder(detailOrder)}
                disabled={marking || deleting}
                title="Xóa đơn nhập: gỡ dòng SP, công nợ NCC và VAT/bảng kê đã gắn cho đơn đã bán (cập nhật lại sổ S1A). Không hoàn tác."
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{deleting ? "hourglass_top" : "delete"}</span>
                {deleting ? "Đang xóa…" : "Xóa đơn"}
              </button>
              <button
                className="btn btn-compact"
                style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, background: "var(--accent, #7c5cff)", color: "#fff", border: "none" }}
                onClick={handleSaveDetailHeader}
                disabled={detailSaving || marking || deleting}
                title="Lưu thông tin đơn (NCC, ngày, tiền, ghi chú) — không sửa dòng SP"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{detailSaving ? "hourglass_top" : "save"}</span>
                {detailSaving ? "Đang lưu…" : "Lưu đơn"}
              </button>
            </div>
            {/* Chứng từ — lên đầu, dễ thấy không phải cuộn */}
            <ImportAttachments orderId={detailOrder.id} />
            <div className="list">
              {(detailOrder.goods_type === "bangke" || detailOrder.goods_type === "vat") && !detailEdit.supplier_tax_id?.trim() && (
                <div className="list-item" style={{ background: "rgba(251,191,36,0.08)", borderRadius: 6 }}>
                  <div className="name" style={{ color: "#fbbf24", fontWeight: 600 }}>
                    <Badge variant="warn">Thiếu MST</Badge>
                  </div>
                  <div className="right" style={{ fontSize: 11, color: "var(--text-secondary)" }}>Đơn {GOODS_TYPE_LABELS[detailOrder.goods_type as GoodsType] || detailOrder.goods_type} cần MST/CCCD người bán</div>
                </div>
              )}
              <div className="list-item" style={{ flexWrap: "wrap" }}>
                <div className="name">Nhà cung cấp</div>
                <div className="right" style={{ flex: 1, minWidth: 200, maxWidth: 320, display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                  <input value={detailEdit.supplier_name} autoComplete="off" onChange={(e) => setDetailEdit({ ...detailEdit, supplier_name: e.target.value })} style={{ width: "100%", textAlign: "right" }} />
                  <div style={{ width: "100%" }}>
                    <Select<string>
                      value=""
                      options={[{ value: "", label: "— Chọn NCC chuẩn để fill —" }, ...supplierList.map((s) => ({ value: s.id, label: s.name + (s.tax_id ? ` · MST ${s.tax_id}` : "") }))]}
                      onChange={(v) => { if (!v) return; const s = supplierList.find((x) => x.id === v); if (s) pickDetailSupplier(s); }}
                      ariaLabel="Chọn NCC chuẩn"
                      searchable
                      searchPlaceholder="Tìm NCC…"
                    />
                  </div>
                </div>
              </div>
              <div className="list-item">
                <div className="name">Số ĐT</div>
                <div className="right" style={{ flex: 1, maxWidth: 260 }}>
                  <input value={detailEdit.supplier_phone} autoComplete="off" onChange={(e) => setDetailEdit({ ...detailEdit, supplier_phone: e.target.value })} style={{ width: "100%", textAlign: "right" }} />
                </div>
              </div>
              <div className="list-item">
                <div className="name">Địa chỉ</div>
                <div className="right" style={{ flex: 1, maxWidth: 260 }}>
                  <input value={detailEdit.supplier_address} autoComplete="off" onChange={(e) => setDetailEdit({ ...detailEdit, supplier_address: e.target.value })} style={{ width: "100%", textAlign: "right" }} />
                </div>
              </div>
              <div className="list-item">
                <div className="name">MST/CCCD</div>
                <div className="right" style={{ flex: 1, maxWidth: 260 }}>
                  <input value={detailEdit.supplier_tax_id} autoComplete="off" onChange={(e) => setDetailEdit({ ...detailEdit, supplier_tax_id: e.target.value })} style={{ width: "100%", textAlign: "right" }} />
                </div>
              </div>
              <div className="list-item">
                <div className="name">Số TK</div>
                <div className="right" style={{ flex: 1, maxWidth: 260 }}>
                  <input value={detailEdit.supplier_bank_account} autoComplete="off" onChange={(e) => setDetailEdit({ ...detailEdit, supplier_bank_account: e.target.value })} style={{ width: "100%", textAlign: "right" }} />
                </div>
              </div>
              <div className="list-item">
                <div className="name">Ngày đặt</div>
                <div className="right" style={{ flex: 1, maxWidth: 260 }}>
                  <input type="date" value={detailEdit.order_date} onChange={(e) => setDetailEdit({ ...detailEdit, order_date: e.target.value })} style={{ width: "100%", textAlign: "right" }} />
                </div>
              </div>
              <div className="list-item">
                <div className="name">Tiền tệ / Tỷ giá</div>
                <div className="right">{detailOrder.currency} · x{detailOrder.exchange_rate}</div>
              </div>
              <div className="list-item">
                <div className="name">Tổng tiền</div>
                <div className="right" style={{ flex: 1, maxWidth: 260 }}>
                  <NumField value={detailEdit.total_amount || null} emptyValue={0} onCommit={(v) => setDetailEdit({ ...detailEdit, total_amount: v ?? 0 })} style={{ width: "100%", textAlign: "right", fontWeight: 700 }} />
                </div>
              </div>
              <div className="list-item">
                <div className="name">Đã trả</div>
                <div className="right" style={{ flex: 1, maxWidth: 260 }}>
                  <NumField value={detailEdit.paid_amount || null} emptyValue={0} onCommit={(v) => setDetailEdit({ ...detailEdit, paid_amount: v ?? 0 })} style={{ width: "100%", textAlign: "right", color: "var(--accent-green)" }} />
                </div>
              </div>
              <div className="list-item">
                <div className="name">Còn nợ</div>
                <div className="right" style={{ color: "var(--accent-red)", fontWeight: 700 }}>{formatVND(detailEdit.total_amount - detailEdit.paid_amount)}</div>
              </div>
              {detailEdit.total_amount - detailEdit.paid_amount > 0 && (
                <div className="list-item" style={{ justifyContent: "flex-end" }}>
                  <button
                    className="btn btn-ghost"
                    type="button"
                    style={{ fontSize: 12, padding: "5px 12px", border: "1px solid var(--accent-green)", color: "var(--accent-green)" }}
                    onClick={() => handleMarkPaid({ ...detailOrder, ...detailEdit })}
                    title="Ghi nhận đã trả đủ tiền cho NCC — đồng bộ sang màn Công nợ"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }}>price_check</span>
                    Đánh dấu đã trả đủ
                  </button>
                </div>
              )}
              <div className="list-item" style={{ flexDirection: "column", alignItems: "stretch", gap: 4 }}>
                <div className="name">Ghi chú</div>
                <textarea value={detailEdit.note} onChange={(e) => setDetailEdit({ ...detailEdit, note: e.target.value })} rows={2} style={{ resize: "vertical", width: "100%", fontSize: 12 }} />
              </div>
              {detailOrder.goods_type && (
                <div className="list-item">
                  <div className="name">Loại hàng</div>
                  <div className="right">{GOODS_TYPE_LABELS[detailOrder.goods_type as GoodsType] || detailOrder.goods_type}</div>
                </div>
              )}
              <div className="list-item">
                <div className="name">Số hóa đơn</div>
                <div className="right" style={{ flex: 1, maxWidth: 260 }}>
                  <input value={detailEdit.invoice_no} autoComplete="off" onChange={(e) => setDetailEdit({ ...detailEdit, invoice_no: e.target.value })} style={{ width: "100%", textAlign: "right" }} />
                </div>
              </div>
              <div className="list-item">
                <div className="name">Trạng thái</div>
                <div className="right">
                  <Badge variant={statusVariant(detailOrder.status)}>{statusText(detailOrder.status)}</Badge>
                </div>
              </div>
              {detailOrder.invoice_file_id && (
                <div className="list-item">
                  <div className="name">Hóa đơn đính kèm</div>
                  <div className="right">
                    <button className="btn btn-ghost" type="button" style={{ fontSize: 12, padding: "4px 10px" }} onClick={() => viewInvoice(detailOrder)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>receipt</span>
                      Xem hóa đơn
                    </button>
                  </div>
                </div>
              )}
            </div>

            {detailOrder.items && detailOrder.items.length > 0 && (
              <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Sản phẩm nhập ({detailOrder.items.length})</div>
                <div className="list">
                  {detailOrder.items.map((it) => (
                    <div className="list-item" key={it.id ?? it.item_name} style={{ flexDirection: "column", alignItems: "stretch", gap: 8, padding: "12px 4px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                        <div>
                          <div className="name" style={{ fontSize: 14 }}>{it.item_name}{it.sku ? <span style={{ opacity: 0.55, fontWeight: 400 }}> · {it.sku}</span> : ""}</div>
                          <div className="meta" style={{ fontSize: 12.5, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 3 }}>
                            <span>{it.quantity} × {formatVND(it.unit_cost)}</span>
                            {it.is_vat && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 6, color: "#60a5fa", background: "rgba(96,165,250,0.14)" }}>VAT</span>}
                            {it.is_bangke && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 6, color: "#fbbf24", background: "rgba(251,191,36,0.14)" }}>Bảng kê</span>}
                          </div>
                        </div>
                        <div className="amount" style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap" }}>{formatVND(it.subtotal ?? it.quantity * it.unit_cost)}</div>
                      </div>
                      {it.id != null && detailOrder.status !== "imported" && (
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => openSplit(it)} title="Tách 1 dòng thành nhiều phần (nếu chưa nhập kho)">
                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>call_split</span>
                            Tách dòng
                          </button>
                        </div>
                      )}
                      <DetailMapRow
                        item={it}
                        orderImported={detailOrder.status === "imported"}
                        onSave={handleSaveDetailMap}
                        onToggleNew={handleToggleNew}
                      />
                      {it.mapped_item_id && (detailOrder.goods_type === "vat" || detailOrder.goods_type === "bangke") && (
                        <VatBackfillControl
                          orderId={detailOrder.id}
                          item={it}
                          subType={detailOrder.goods_type === "vat" ? "vat" : "bangke"}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal Tách dòng SP */}
      <Modal open={!!splitItem} onClose={() => setSplitItem(null)} title={splitItem ? `Tách dòng: ${splitItem.item_name}` : ""}>
        {splitItem && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
              Tách 1 dòng (SL gốc: <strong>{splitItem.quantity}</strong>) thành nhiều phần. Tổng SL các phần phải bằng {splitItem.quantity}. Chỉ tách được khi CHƯA nhập kho.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {splitParts.map((p, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "var(--text-tertiary)", minWidth: 48 }}>Phần {i + 1}</span>
                  <input
                    type="number"
                    min={0}
                    placeholder="Số lượng"
                    value={p.quantity}
                    onChange={(e) => setSplitParts((prev) => prev.map((x, j) => j === i ? { quantity: e.target.value } : x))}
                    style={{ flex: 1, fontSize: 13 }}
                    aria-label={`Số lượng phần ${i + 1}`}
                  />
                  {splitParts.length > 2 && (
                    <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px", color: "var(--accent-red)" }} onClick={() => setSplitParts((prev) => prev.filter((_, j) => j !== i))}>×</button>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
              <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setSplitParts((prev) => [...prev, { quantity: "" }])}>+ Thêm phần</button>
              <span style={{ fontSize: 12, fontWeight: 600, color: splitSum === splitTargetQty ? "var(--accent-green)" : "var(--accent-red)" }}>
                Tổng: {splitSum} / {splitTargetQty}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost" onClick={() => setSplitItem(null)}>Huỷ</button>
              <button className="btn btn-primary" onClick={handleSplit} disabled={!splitValid || splitSubmitting}>
                {splitSubmitting ? "Đang tách…" : "Tách dòng"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

// Hàng inline để map mỗi dòng SP nhập với 1 SP POS đang bán.
// Dùng trong modal chi tiết — đơn đã imported vẫn sửa được (phục vụ truy ngược khai thuế).
function DetailMapRow({
  item,
  orderImported = false,
  onSave,
  onToggleNew,
}: {
  item: ImportOrderItem;
  orderImported?: boolean;
  onSave: (itemId: number, mappedId: string | null, mappedName?: string) => void;
  onToggleNew: (itemId: number, isNew: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Array<{ id: string; item_name: string; sku?: string }>>([]);

  useEffect(() => {
    if (editing && item.mapped_item_id) setQuery(""); // mở editor thì gõ lại
  }, [editing, item.mapped_item_id]);

  const runSearch = async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    try {
      const items = (await searchCatalog(q, 6)) ?? (await api.items.search(q)).items;
      setResults((items || []).slice(0, 6).map((p) => ({ id: p.id, item_name: p.item_name, sku: p.sku })));
    } catch { setResults([]); }
  };

  if (item.id == null) return null;
  const itemId = item.id;

  // Đã đánh dấu hàng mới → badge. Imported + chưa map = chưa tạo SP (không hứa "sẽ tạo" mơ hồ).
  if (item.is_new_product) {
    const stuckUnmapped = orderImported && !item.mapped_item_id;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 2 }}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 600,
          padding: "4px 10px", borderRadius: 999,
          color: stuckUnmapped ? "#fbbf24" : "#34d399",
          background: stuckUnmapped ? "rgba(251,191,36,0.14)" : "rgba(52,211,153,0.12)",
          border: `1px solid ${stuckUnmapped ? "rgba(251,191,36,0.45)" : "rgba(52,211,153,0.35)"}`,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
            {stuckUnmapped ? "warning" : "add_box"}
          </span>
          {stuckUnmapped
            ? "Chưa tạo SP — bấm Tạo SP còn lại"
            : "Hàng mới — nhập kho sẽ tạo SP"}
        </span>
        <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 12px", minHeight: 36, color: "var(--accent-red)" }} onClick={() => onToggleNew(itemId, false)} title="Bỏ đánh dấu hàng mới — chuyển lại sang map với SP POS đang bán">
          Bỏ đánh dấu
        </button>
      </div>
    );
  }

  if (!editing) {
    const mapped = !!item.mapped_item_id;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 2 }}>
        <span
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 600,
            padding: "4px 10px", borderRadius: 999,
            color: mapped ? "var(--accent-green)" : "var(--text-tertiary)",
            background: mapped ? "rgba(52,211,153,0.12)" : "rgba(148,163,184,0.10)",
            border: `1px solid ${mapped ? "rgba(52,211,153,0.35)" : "var(--border-subtle)"}`,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{mapped ? "check_circle" : "link_off"}</span>
          {mapped ? "Đã map SP POS" : "Chưa map"}
        </span>
        <button
          type="button"
          className="btn btn-ghost"
          style={{ fontSize: 13, padding: "8px 14px", minHeight: 40, display: "inline-flex", alignItems: "center", gap: 6 }}
          onClick={() => setEditing(true)}
          title="Map dòng này với SP POS đang bán — để truy ngược nguồn khi khai thuế"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{mapped ? "edit" : "add_link"}</span>
          {mapped ? "Sửa map" : "Map SP POS"}
        </button>
        {!mapped && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: 13, padding: "8px 14px", minHeight: 40, display: "inline-flex", alignItems: "center", gap: 6, color: "#34d399" }}
            onClick={() => onToggleNew(itemId, true)}
            title="Hàng mới chưa có trên POS — khi nhập kho sẽ tạo SP mới"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_box</span>
            Hàng mới
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "relative", marginTop: 6 }}>
      <label style={{ display: "block", fontSize: 12, color: "var(--text-tertiary)", marginBottom: 4 }}>
        Tìm SP POS đang bán để liên kết
      </label>
      <div style={{ position: "relative" }}>
        <span className="material-symbols-outlined" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 18, color: "var(--text-tertiary)", pointerEvents: "none" }}>search</span>
        <input
          autoFocus
          placeholder="Gõ tên SP POS (≥ 2 ký tự)…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); runSearch(e.target.value); }}
          onBlur={() => setTimeout(() => setResults([]), 200)}
          style={{ fontSize: 14, width: "100%", padding: "10px 12px 10px 36px", minHeight: 44, borderRadius: 8 }}
        />
      </div>
      {results.length > 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: 8, zIndex: 200, maxHeight: 200, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
          {results.map((r) => (
            <div
              key={r.id}
              role="button"
              style={{ padding: "10px 12px", cursor: "pointer", fontSize: 13.5, minHeight: 44, display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid var(--border-subtle)" }}
              onMouseDown={() => { onSave(item.id!, r.id, r.item_name); setEditing(false); }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(124,158,255,0.10)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--text-tertiary)" }}>inventory_2</span>
              <span>{r.item_name}{r.sku ? <span style={{ opacity: 0.6 }}> · {r.sku}</span> : ""}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        {item.mapped_item_id && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: 13, padding: "8px 14px", minHeight: 40, color: "var(--accent-red)", display: "inline-flex", alignItems: "center", gap: 6 }}
            onClick={() => { onSave(item.id!, null); setEditing(false); }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>link_off</span>
            Bỏ map
          </button>
        )}
        <button
          type="button"
          className="btn btn-ghost"
          style={{ fontSize: 13, padding: "8px 14px", minHeight: 40 }}
          onClick={() => setEditing(false)}
        >Đóng</button>
      </div>
    </div>
  );
}

// Áp dụng VAT/bảng kê cho các đơn ĐÃ BÁN của SP được map — theo số lượng mua (FIFO).
// preview trước (xem danh sách đơn + qty), rồi apply (ghi + regenerate S1A; có thể force qua kỳ khóa).
function VatBackfillControl({ orderId, item, subType }: { orderId: number; item: ImportOrderItem; subType: "vat" | "bangke" }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState<string>(String(item.quantity || ""));
  const [unitsPerPack, setUnitsPerPack] = useState<string>("1");
  const [preview, setPreview] = useState<VatBackfillResult | null>(null);
  const [loading, setLoading] = useState(false);
  const label = subType === "vat" ? "VAT" : "Bảng kê";
  // SL thực = SL mua (theo đơn vị nhập) × số cái mỗi đơn vị (vd mua 1 set = 12 cái).
  const totalUnits = Math.max(0, Number(qty) || 0) * Math.max(1, Number(unitsPerPack) || 1);

  const runPreview = async () => {
    if (!item.mapped_item_id || totalUnits <= 0) {
      toast.error("Nhập số lượng mua có hóa đơn (> 0)");
      return;
    }
    setLoading(true);
    try {
      const r = await api.importOrders.vatBackfill(orderId, {
        mapped_item_id: item.mapped_item_id,
        import_order_item_id: item.id ?? null,
        sub_type: subType,
        purchased_qty: totalUnits,
        preview: true,
      });
      setPreview(r);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi xem trước");
    }
    setLoading(false);
  };

  const runApply = async () => {
    if (!preview || !item.mapped_item_id) return;
    const hasLocked = preview.locked_dates.length > 0;
    const message = hasLocked
      ? `Sẽ gắn ${label} cho các đơn đã bán (tổng ${preview.allocated_total} sp).\n\n⚠️ Có ${preview.locked_dates.length} ngày thuộc kỳ S1A ĐÃ KHÓA: ${preview.locked_dates.join(", ")}. Áp dụng sẽ GHI ĐÈ kỳ đã khóa.`
      : `Gắn ${label} cho các đơn đã bán (tổng ${preview.allocated_total} sp) và cập nhật sổ S1A?`;
    const force = hasLocked;
    if (!await confirm({ title: `Áp dụng ${label} cho đơn đã bán`, message, danger: hasLocked, confirmLabel: hasLocked ? "Ghi đè & cập nhật" : "Áp dụng" })) return;
    setLoading(true);
    try {
      const r = await api.importOrders.vatBackfill(orderId, {
        mapped_item_id: item.mapped_item_id,
        import_order_item_id: item.id ?? null,
        sub_type: subType,
        purchased_qty: totalUnits,
        preview: false,
        force_locked: force,
      });
      toast.success(`Đã gắn ${label} cho ${r.allocated_total} sp · cập nhật S1A ${r.affected_dates.length} ngày`);
      setPreview(r);
      setOpen(false);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi áp dụng");
    }
    setLoading(false);
  };

  const runRemove = async () => {
    if (!item.mapped_item_id) return;
    if (!await confirm({ title: `Gỡ ${label} đã gắn`, message: `Gỡ toàn bộ ${label} đã gắn cho SP này (từ đơn nhập #${orderId}) và cập nhật lại sổ S1A?`, danger: true, confirmLabel: "Gỡ" })) return;
    setLoading(true);
    try {
      const r = await api.importOrders.vatBackfillRemove(orderId, {
        mapped_item_id: item.mapped_item_id,
        sub_type: subType,
        scope: "order",
        force_locked: true,
      });
      toast.success(r.removed_count > 0 ? `Đã gỡ ${r.removed_qty} sp · cập nhật S1A ${r.affected_dates.length} ngày` : "Không có gì để gỡ");
      setPreview(null);
      setOpen(false);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi gỡ");
    }
    setLoading(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn-ghost"
        style={{ fontSize: 13, padding: "8px 14px", minHeight: 40, alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, color: "#a78bfa", border: "1px solid rgba(167,139,250,0.35)", background: "rgba(167,139,250,0.08)" }}
        onClick={() => setOpen(true)}
        title={`Gắn ${label} cho các đơn đã bán SP này (theo số lượng mua), cập nhật vào sổ S1A`}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>receipt_long</span>
        Áp dụng {label} cho đơn đã bán
      </button>
    );
  }

  return (
    <div style={{ border: "1px solid rgba(167,139,250,0.3)", borderRadius: 10, padding: 12, marginTop: 6, fontSize: 13, background: "rgba(167,139,250,0.06)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ opacity: 0.85 }}>SL mua (đơn vị nhập):</span>
        <input
          type="text"
          inputMode="numeric"
          value={qty}
          onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))}
          style={{ width: 80, fontSize: 14, padding: "8px 10px", minHeight: 40, borderRadius: 8 }}
        />
        <span style={{ opacity: 0.85 }}>× số cái/đơn vị:</span>
        <input
          type="text"
          inputMode="numeric"
          value={unitsPerPack}
          onChange={(e) => setUnitsPerPack(e.target.value.replace(/\D/g, ""))}
          title="VD: mua 1 set chứa 12 cái → gõ 12. Mua lẻ từng cái → để 1."
          style={{ width: 70, fontSize: 14, padding: "8px 10px", minHeight: 40, borderRadius: 8 }}
        />
        <span style={{ opacity: 0.7 }}>= <b style={{ color: "var(--accent-green)" }}>{totalUnits}</b> cái</span>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <button type="button" className="btn btn-ghost" style={{ fontSize: 13, padding: "8px 14px", minHeight: 40 }} onClick={runPreview} disabled={loading} title={`Xem trước các đơn đã bán sẽ được gắn ${label} (FIFO theo ngày) — chưa ghi gì`}>
          {loading ? "…" : "Xem trước"}
        </button>
        <button type="button" className="btn btn-ghost" style={{ fontSize: 13, padding: "8px 14px", minHeight: 40, color: "var(--accent-red)" }} onClick={runRemove} disabled={loading} title="Gỡ toàn bộ allocation đã gắn cho SP này trong đơn nhập này">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
          Gỡ đã gắn
        </button>
        <button type="button" className="btn btn-ghost" style={{ fontSize: 13, padding: "8px 14px", minHeight: 40 }} onClick={() => { setOpen(false); setPreview(null); }}>Đóng</button>
      </div>
      {preview && (
        <div>
          <div style={{ opacity: 0.85, marginBottom: 4 }}>
            Tồn hiện tại: <b>{preview.current_stock}</b> · Đã bán tổng: <b>{preview.total_sold_qty}</b> · Đã gắn trước đó: <b>{preview.already_allocated}</b> · Sẽ gắn lần này: <b style={{ color: "var(--accent-green)" }}>{preview.allocated_total}</b>
          </div>
          <div style={{ opacity: 0.6, fontSize: 11.5, marginBottom: 6 }}>
            Đối chiếu: đã bán {preview.total_sold_qty} + tồn {preview.current_stock} = {preview.total_sold_qty + preview.current_stock} (so với tổng đã nhập)
          </div>
          {preview.locked_dates.length > 0 && (
            <div style={{ color: "var(--accent-red)", marginBottom: 4 }}>
              ⚠️ {preview.locked_dates.length} ngày thuộc kỳ S1A đã khóa — Áp dụng sẽ ghi đè.
            </div>
          )}
          <div style={{ maxHeight: 140, overflowY: "auto", border: "1px solid var(--border-subtle)", borderRadius: 4 }}>
            {preview.rows.length === 0 ? (
              <div style={{ padding: 6, opacity: 0.6 }}>Chưa có đơn bán nào của SP này.</div>
            ) : preview.rows.map((r, i) => (
              <div key={r.receipt_item_id + i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 6px", borderBottom: "1px solid var(--border-subtle)" }}>
                <span>{formatDate(r.receipt_date)} · {r.receipt_number}{r.locked ? " 🔒" : ""}</span>
                <span>bán {r.sale_qty} → gắn <b style={{ color: r.alloc_qty > 0 ? "var(--accent-green)" : "inherit" }}>{r.alloc_qty}</b></span>
              </div>
            ))}
          </div>
          <button type="button" className="btn" style={{ fontSize: 11, padding: "4px 10px", minHeight: 28, marginTop: 6 }} onClick={runApply} disabled={loading || preview.allocated_total === 0} title={`Ghi ${label} cho các đơn đã bán ở trên và cập nhật lại sổ S1A`}>
            {loading ? "Đang áp dụng..." : `Áp dụng & cập nhật S1A`}
          </button>
        </div>
      )}
    </div>
  );
}
